// src/layouts/annotator/exams/annotation.js
// Dynamic: fetch exam by exam_id, build tabs from sample types, list recordings
// Shows per-recording status & enables "Annotate" if *your* status !== "Submitted"

import { useEffect, useMemo, useState, useCallback, useRef, memo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CommentIcon from "@mui/icons-material/Comment";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import WaveSurfer from "wavesurfer.js";
import RegionsPlugin from "wavesurfer.js/dist/plugins/regions.esm.js";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useTranslation } from "hooks/useTranslation";
import {
  getExamBundle,
  saveAnnotation,
  getAnnotation,
  getAnnotationsBulk,
  markExamComplete,
  saveAnnotationsBulk,
  resolveRemark,
} from "services/sampleService";
import { API_BASE } from "config/api";

const proxiedAudioUrl = (originalUrl) => {
  if (!originalUrl) return originalUrl;
  const url = new URL("api/audio/proxy", API_BASE);
  url.searchParams.set("url", originalUrl);
  return url.toString();
};

// --- Constants and Utility Functions ---

const useQuery = () => new URLSearchParams(useLocation().search);

function getLoggedInAnnotatorId() {
  const candidates = [localStorage.getItem("user_id")].filter(Boolean);
  if (!candidates.length) return null;
  const v = candidates[0];
  const n = Number(v);
  return Number.isNaN(n) ? v : n;
}

const readCorrectOptions = ["Yes", "No"];
const fluencyOptions = ["Yes", "No"];
const bgNoiseOptions = ["Motor Bike", "Machine Noise", "Animals", "Other"];
const notTranscribableOptions = ["Blank Audio", "Too Noisy", "Language Mismatch", "Other"];

// Helper function to fix UTF-8 encoding issues
const fixUTF8Encoding = (text) => {
  if (!text) return text;

  try {
    // Check if text has encoding issues (contains Ã characters)
    if (/Ã[±³©­]/g.test(text)) {
      // Common replacements for Spanish characters
      return text
        .replace(/Ã±/g, "ñ")
        .replace(/Ã³/g, "ó")
        .replace(/Ã©/g, "é")
        .replace(/Ã­/g, "í")
        .replace(/Ã¡/g, "á")
        .replace(/Ãº/g, "ú")
        .replace(/Ã\u00AD/g, "í")
        .replace(/Ã\u00B3/g, "ó");
    }
    return text;
  } catch (e) {
    console.error("Error fixing encoding:", e);
    return text;
  }
};

// Helper function to split text into lines
// Passage rules:
// 1. Line 1 = title
// 2. Line 2 = empty → remove it
// 3. Line 3 = passage → split by full stop, ignore last punctuation
const splitTextIntoLines = (text, isPassage = false) => {
  if (text == null) {
    return [""];
  }

  // Fix encoding issues before processing
  const fixedText = fixUTF8Encoding(text);
  const strText = String(fixedText).trim();
  if (strText === "") {
    return [""];
  }

  // Always split by newline first
  let lines = strText.split("\n").map((line) => line.trim());

  if (!isPassage) {
    return lines;
  }

  // ---- PASSAGE HANDLING ----

  // A. Line 1 is title
  const title = lines[0] || "";

  // B. Remove line 2 if it is empty
  let passageLineIndex = 1;
  if (lines[1] === "") {
    passageLineIndex = 2;
  }

  const passageText = lines.slice(passageLineIndex).join(" ").trim();

  if (!passageText) {
    return [title];
  }

  // C. Split passage by full stop and ignore trailing punctuation
  const sentences = passageText
    .match(/[^.]+\.?/g)
    .map((s) => s.trim())
    .filter(Boolean);

  return [title, ...sentences];
};

// Helper function to join lines back into a single text WITH POSITION PRESERVATION
const joinLinesIntoText = (lines) => {
  if (!lines || !Array.isArray(lines) || lines.length === 0) {
    return "";
  }

  //if line is empty string , add hypen only in case of passage or para

  if (lines.length > 1) {
    lines = lines.map((line) => (line == "" ? "-" : line));
  }

  // Join with newline character to preserve line positions
  // Empty lines become actual newlines
  return lines.join("\n");
};

// --- Copy Button Component ---
const CopyButton = ({ onClick, disabled = false, title = "Copy to transcription" }) => (
  <Tooltip title={title} placement="top">
    <IconButton
      size="small"
      onClick={onClick}
      disabled={disabled}
      sx={{
        position: "absolute",
        right: 4,
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 1,
        backgroundColor: "white",
        "&:hover": {
          backgroundColor: "#f5f5f5",
        },
        width: 28,
        height: 28,
      }}
    >
      <ContentCopyIcon fontSize="small" />
    </IconButton>
  </Tooltip>
);

CopyButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  title: PropTypes.string,
};

// --- Select Field Component ---

const SelectField = ({
  label,
  field,
  options,
  defaultValue,
  selectedValue,
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [openDropdown, setOpenDropdown] = useState(false);
  const getOptionLabel = (opt) => {
    if (field === "read_correctly") {
      return t(`annotator.annotation.options.readCorrect.${String(opt).toLowerCase()}`, opt);
    }
    if (field === "bg_noise") {
      const noiseKey = String(opt).toLowerCase().replace(/\s+/g, "");
      return t(`annotator.annotation.options.bgNoise.${noiseKey}`, opt);
    }
    if (field === "not_transcribable") {
      const reasonMap = {
        "Blank Audio": "blankAudio",
        "Too Noisy": "tooNoisy",
        "Language Mismatch": "languageMismatch",
        Other: "other",
      };
      return t(`annotator.annotation.options.notTranscribable.${reasonMap[opt] || "other"}`, opt);
    }
    return opt;
  };

  const handleSelectChange = (e) => {
    if (!onChange) return;
    const raw = e?.target?.value;
    // Treat picking the "Select …" placeholder as clearing the field.
    const normalized = raw === defaultValue ? "" : raw;
    onChange({ target: { value: normalized } });
  };

  return (
    <FormControl fullWidth sx={{ height: 50 }}>
      <Select
        className="pointer"
        labelId={`${field}-label`}
        value={selectedValue ?? defaultValue}
        onChange={handleSelectChange}
        onOpen={() => setOpenDropdown(true)}
        onClose={() => setOpenDropdown(false)}
        IconComponent={() => <FontAwesomeIcon icon={openDropdown ? faChevronUp : faChevronDown} />}
        disabled={disabled}
        sx={{
          height: 50,
          fontSize: "0.8rem",
          "& svg": {
            top: "calc(50% - 0.5em)",
            right: 10,
            position: "absolute",
            pointerEvents: "none",
          },
        }}
      >
        <MenuItem value={defaultValue}>{defaultValue}</MenuItem>
        {options.map((opt, i) => (
          <MenuItem key={i} value={opt}>
            {getOptionLabel(opt)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

SelectField.propTypes = {
  label: PropTypes.string.isRequired,
  field: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  defaultValue: PropTypes.string.isRequired,
  selectedValue: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

// --- Header Row Component (conditional columns) ---
const HeaderRow = ({ showDisfluency = false, showMistakes = false }) => {
  const { t } = useTranslation();
  return (
    <Grid container spacing={2} alignItems="center" py={1} borderBottom="1px solid #e6e6e6">
      <Grid item xs={3}>
        <Box display="flex" alignItems="center" gap={1} justifyContent="center">
          <Typography fontWeight="bold" fontSize="0.875rem">
            {t("annotator.annotation.headers.recording", "Recording")}
          </Typography>
          <Tooltip
            title={t("annotator.annotation.tooltips.playAudio", "Play the audio for this sample")}
            placement="top"
          >
            <InfoOutlinedIcon fontSize="small" />
          </Tooltip>
        </Box>
      </Grid>

      <Grid item xs={1.5}>
        <Box display="flex" alignItems="center" gap={1} justifyContent="center">
          <Typography fontWeight="bold" fontSize="0.875rem">
            {t("annotator.annotation.headers.canonicalText", "Canonical Text")}
          </Typography>
          <Tooltip
            title={t(
              "annotator.annotation.tooltips.canonicalText",
              "Expected/reference text for the recording"
            )}
            placement="top"
          >
            <InfoOutlinedIcon fontSize="small" />
          </Tooltip>
        </Box>
      </Grid>

      <Grid item xs={1.5}>
        <Box display="flex" alignItems="center" gap={1} justifyContent="center">
          <Typography fontWeight="bold" fontSize="0.875rem">
            {t("annotator.annotation.headers.transcribedText", "Transcribed Text")}
          </Typography>
          <Tooltip
            title={t(
              "annotator.annotation.tooltips.transcribedText",
              "What you transcribe from the audio (enter line by line)"
            )}
            placement="top"
          >
            <InfoOutlinedIcon fontSize="small" />
          </Tooltip>
        </Box>
      </Grid>

      <Grid item xs={1.25}>
        <Box display="flex" alignItems="center" gap={1} justifyContent="center">
          <Typography fontWeight="bold" fontSize="0.875rem">
            {t("annotator.annotation.headers.startEnd", "Start / End")}
          </Typography>
          <Tooltip
            title={t(
              "annotator.annotation.tooltips.startEnd",
              "Mark the audio timestamp where this item starts and ends"
            )}
            placement="top"
          >
            <InfoOutlinedIcon fontSize="small" />
          </Tooltip>
        </Box>
      </Grid>

      <Grid item xs={1.25}>
        <Box display="flex" alignItems="center" gap={1} justifyContent="center">
          <Typography fontWeight="bold" fontSize="0.875rem">
            {t("annotator.annotation.headers.readCorrectly", "Read Correctly?")}
          </Typography>
          <Tooltip
            title={t("annotator.annotation.tooltips.readCorrectly", "Was pronunciation correct?")}
            placement="top"
          >
            <InfoOutlinedIcon fontSize="small" />
          </Tooltip>
        </Box>
      </Grid>

      {showMistakes && (
        <Grid item xs={1}>
          <Box display="flex" alignItems="center" gap={1} justifyContent="center">
            <Typography fontWeight="bold" fontSize="0.875rem">
              {t("annotator.annotation.headers.mistakes", "No. of mistakes")}
            </Typography>
            <Tooltip
              title={t(
                "annotator.annotation.tooltips.mistakes",
                "Number of pronunciation/word errors in the sample"
              )}
              placement="top"
            >
              <InfoOutlinedIcon fontSize="small" />
            </Tooltip>
          </Box>
        </Grid>
      )}

      <Grid item xs={1.25}>
        <Box display="flex" alignItems="center" gap={1} justifyContent="center">
          <Typography fontWeight="bold" fontSize="0.875rem">
            {t("annotator.annotation.headers.bgNoise", "BG Noise")}
          </Typography>
          <Tooltip
            title={t("annotator.annotation.tooltips.bgNoise", "Background noise type if any")}
            placement="top"
          >
            <InfoOutlinedIcon fontSize="small" />
          </Tooltip>
        </Box>
      </Grid>

      <Grid item xs={1.5}>
        <Box display="flex" alignItems="center" gap={1} justifyContent="center">
          <Typography fontWeight="bold" fontSize="0.875rem">
            {t("annotator.annotation.headers.nonTranscribable", "Non-Transcribable")}
          </Typography>
          <Tooltip
            title={t(
              "annotator.annotation.tooltips.nonTranscribable",
              "Reason if the audio can't be transcribed"
            )}
            placement="top"
          >
            <InfoOutlinedIcon fontSize="small" />
          </Tooltip>
        </Box>
      </Grid>

      {showDisfluency && (
        <Grid item xs={0.75}>
          <Box display="flex" alignItems="center" gap={1} justifyContent="center">
            <Typography fontWeight="bold" fontSize="0.875rem">
              {t("annotator.annotation.headers.disfluency", "Disfluency Occurences")}
            </Typography>
            <Tooltip
              title={t(
                "annotator.annotation.tooltips.disfluency",
                "Number of disfluency occurrences in the recording"
              )}
              placement="top"
            >
              <InfoOutlinedIcon fontSize="small" />
            </Tooltip>
          </Box>
        </Grid>
      )}

      <Grid item xs={0.75}>
        <Typography fontWeight="bold" align="center" fontSize="0.875rem">
          {t("annotator.annotation.headers.actions", "Actions")}
        </Typography>
      </Grid>
    </Grid>
  );
};

HeaderRow.propTypes = {
  showDisfluency: PropTypes.bool,
  showMistakes: PropTypes.bool,
};

// --- Annotation Row Component ---
// Memo comparator: re-render a row only when its own data changes. Function
// props are compared except onSaveRecord (recreated every parent render); a
// row always receives fresh props whenever it does re-render, and saving an
// unchanged row reads that row's unchanged data, so skipping it is safe.
const areRowPropsEqual = (prev, next) =>
  prev.row === next.row &&
  prev.index === next.index &&
  prev.annotatorId === next.annotatorId &&
  prev.isExamSubmitted === next.isExamSubmitted &&
  prev.savingRecord[prev.row.recording_id] === next.savingRecord[next.row.recording_id] &&
  prev.onAnnotationChange === next.onAnnotationChange &&
  prev.canAnnotate === next.canAnnotate &&
  prev.onViewRemark === next.onViewRemark;

function AnnotationRowBase({
  row,
  annotatorId,
  onAnnotationChange,
  canAnnotate,
  savingRecord,
  onSaveRecord,
  isExamSubmitted,
  onViewRemark,
  index,
}) {
  const { t } = useTranslation();
  const disabled = !canAnnotate(row) || isExamSubmitted;
  const isPassage = row?.is_passage === 1 || row?.is_passage === "1";

  const canonicalLines = useMemo(
    () => splitTextIntoLines(row.sample_text, isPassage),
    [row.sample_text, isPassage]
  );

  const transcriptionLines = useMemo(() => {
    const lines = row.annotation.transcription_lines || [];
    const diff = canonicalLines.length - lines.length;
    if (diff > 0) return [...lines, ...Array(diff).fill("")]; // Use empty strings
    return lines.slice(0, canonicalLines.length);
  }, [row.annotation.transcription_lines, canonicalLines]);

  const audioRef = useRef(null);
  const hiddenAudioRef = useRef(null);
  const waveContainerRef = useRef(null);
  const wsRef = useRef(null);
  const regionsPluginRef = useRef(null);
  const rowRootRef = useRef(null);
  const pendingPlayRef = useRef(false);
  const isPlayingRef = useRef(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [shouldInitWave, setShouldInitWave] = useState(false);
  const [waveReady, setWaveReady] = useState(false);
  const [waveError, setWaveError] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Lazy-load audio: only create WaveSurfer (which downloads + decodes the
  // whole file) when the row is near the viewport; tear it down when it
  // scrolls far away, unless it is currently playing.
  useEffect(() => {
    const el = rowRootRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShouldInitWave(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldInitWave(true);
        } else if (!isPlayingRef.current) {
          setShouldInitWave(false);
        }
      },
      { rootMargin: "300px 0px 300px 0px", threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const formatMMSS = (s) => {
    if (!s || Number.isNaN(s)) return "00:00";
    const total = Math.floor(s);
    const mm = String(Math.floor(total / 60)).padStart(2, "0");
    const ss = String(total % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  };

  useEffect(() => {
    if (!shouldInitWave) return undefined;
    if (!waveContainerRef.current || !hiddenAudioRef.current) return undefined;

    setWaveReady(false);
    setWaveError(null);

    const regionsPlugin = RegionsPlugin.create();

    const ws = WaveSurfer.create({
      container: waveContainerRef.current,
      media: hiddenAudioRef.current,
      waveColor: "#bdbdbd",
      progressColor: "#1976d2",
      cursorColor: "#1565c0",
      height: 60,
      barWidth: 2,
      barGap: 1,
      barRadius: 1,
      normalize: true,
      plugins: [regionsPlugin],
    });

    const onPlay = () => {
      isPlayingRef.current = true;
      setIsAudioPlaying(true);
    };
    const onPause = () => {
      isPlayingRef.current = false;
      setIsAudioPlaying(false);
    };
    const onFinish = () => {
      isPlayingRef.current = false;
      setIsAudioPlaying(false);
    };
    const onReady = () => {
      setWaveReady(true);
      setDuration(ws.getDuration() || 0);
      // Play was requested before the instance existed (lazy init).
      if (pendingPlayRef.current) {
        pendingPlayRef.current = false;
        ws.play();
      }
    };
    const onTimeUpdate = (t) => setCurrentTime(t || 0);
    const onError = (err) => {
      console.error("WaveSurfer error:", err);
      setWaveError(String(err?.message || err));
    };

    ws.on("play", onPlay);
    ws.on("pause", onPause);
    ws.on("finish", onFinish);
    ws.on("ready", onReady);
    ws.on("timeupdate", onTimeUpdate);
    ws.on("error", onError);

    wsRef.current = ws;
    regionsPluginRef.current = regionsPlugin;
    audioRef.current = {
      get currentTime() {
        return ws.getCurrentTime();
      },
      pause: () => ws.pause(),
    };

    return () => {
      wsRef.current = null;
      regionsPluginRef.current = null;
      audioRef.current = null;
      isPlayingRef.current = false;
      setWaveReady(false);
      setIsAudioPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      ws.destroy();
    };
  }, [shouldInitWave, isPassage, row.recording_file]);

  // Parse "mm:ss:sss" back to seconds (float)
  const parseTs = (ts) => {
    if (ts == null || ts === "") return null;
    const parts = String(ts).split(":").map(Number);
    if (parts.some(Number.isNaN)) return null;
    const [m, s, ms] = parts;
    return (m || 0) * 60 + (s || 0) + (ms || 0) / 1000;
  };

  const togglePlay = () => {
    if (wsRef.current) {
      if (wsRef.current.isPlaying()) wsRef.current.pause();
      else wsRef.current.play();
      return;
    }
    // WaveSurfer not created yet (lazy init) — request it and play when ready.
    pendingPlayRef.current = true;
    setShouldInitWave(true);
  };

  const audioTimestamps = useMemo(() => {
    const ts = row.annotation.audio_timestamps || [];
    const normalized = ts.map((it) =>
      it && typeof it === "object" ? it : { start: null, end: null }
    );
    const diff = canonicalLines.length - normalized.length;
    if (diff > 0) {
      return [...normalized, ...Array.from({ length: diff }, () => ({ start: null, end: null }))];
    }
    return normalized.slice(0, canonicalLines.length);
  }, [row.annotation.audio_timestamps, canonicalLines]);

  // Sync regions on the waveform with captured start/end timestamps
  useEffect(() => {
    if (!waveReady || !regionsPluginRef.current) return;
    const regions = regionsPluginRef.current;
    regions.clearRegions();
    audioTimestamps.forEach((ts, i) => {
      const start = parseTs(ts?.start);
      const end = parseTs(ts?.end);
      const label = `${i + 1}`;
      if (start != null && end != null && end > start) {
        regions.addRegion({
          start,
          end,
          content: label,
          color: "rgba(25, 118, 210, 0.18)",
          drag: false,
          resize: false,
        });
      } else if (start != null) {
        regions.addRegion({
          start,
          end: start + 0.05,
          content: `${label}▶`,
          color: "rgba(34, 197, 94, 0.55)",
          drag: false,
          resize: false,
        });
      } else if (end != null) {
        regions.addRegion({
          start: Math.max(0, end - 0.05),
          end,
          content: `${label}◀`,
          color: "rgba(239, 68, 68, 0.55)",
          drag: false,
          resize: false,
        });
      }
    });
  }, [audioTimestamps, waveReady]);

  const formatTs = (s) => {
    if (s == null) return null;
    const totalMs = Math.round(s * 1000);
    const m = Math.floor(totalMs / 60000);
    const sec = Math.floor((totalMs % 60000) / 1000);
    const ms = totalMs % 1000;
    return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}:${String(ms).padStart(
      3,
      "0"
    )}`;
  };

  const handleMarkStart = (lineIndex) => {
    if (disabled || !audioRef.current) return;
    const t = audioRef.current.currentTime;
    const updated = audioTimestamps.map((it, i) =>
      i === lineIndex ? { ...(it || {}), start: formatTs(t) } : it
    );
    onAnnotationChange(row.recording_id, "audio_timestamps", updated);
  };

  const handleMarkEnd = (lineIndex) => {
    if (disabled || !audioRef.current) return;
    const t = audioRef.current.currentTime;
    const updated = audioTimestamps.map((it, i) =>
      i === lineIndex ? { ...(it || {}), end: formatTs(t) } : it
    );
    onAnnotationChange(row.recording_id, "audio_timestamps", updated);
    audioRef.current.pause();
  };

  const handleClearStart = (lineIndex) => {
    if (disabled) return;
    const updated = audioTimestamps.map((it, i) =>
      i === lineIndex ? { ...(it || {}), start: null } : it
    );
    onAnnotationChange(row.recording_id, "audio_timestamps", updated);
  };

  const handleClearEnd = (lineIndex) => {
    if (disabled) return;
    const updated = audioTimestamps.map((it, i) =>
      i === lineIndex ? { ...(it || {}), end: null } : it
    );
    onAnnotationChange(row.recording_id, "audio_timestamps", updated);
  };

  // Function to handle copy from canonical to transcription
  const handleCopyToTranscription = (lineIndex) => {
    if (disabled) return;

    const canonicalText = canonicalLines[lineIndex];
    if (!canonicalText) return;

    const updatedTranscriptionLines = [...transcriptionLines];
    updatedTranscriptionLines[lineIndex] = canonicalText;

    onAnnotationChange(row.recording_id, "transcription_lines", updatedTranscriptionLines);
  };

  // Function to copy all canonical text to all transcription lines
  const handleCopyAllToTranscription = () => {
    if (disabled) return;

    const updatedTranscriptionLines = canonicalLines.map((line) => line);
    onAnnotationChange(row.recording_id, "transcription_lines", updatedTranscriptionLines);
  };

  /* ================= PASSAGE LAYOUT ================= */
  if (isPassage) {
    return (
      <Box ref={rowRootRef} py={2} borderBottom="1px solid #eee">
        {/* Recording (fixed at top) */}
        <Box
          mb={2}
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 5,
            bgcolor: "#fff",
            py: 1,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <IconButton
            onClick={togglePlay}
            disabled={disabled}
            color="primary"
            aria-label={isAudioPlaying ? "pause" : "play"}
            sx={{
              border: "1px solid #1976d2",
              "&.Mui-disabled": { borderColor: "#bbb" },
            }}
          >
            {isAudioPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              ref={waveContainerRef}
              sx={{
                width: "100%",
                minHeight: 60,
                bgcolor: "#fafafa",
                borderRadius: 1,
              }}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 0.5,
                minHeight: 18,
              }}
            >
              <Box sx={{ flex: 1 }}>
                {!waveReady && !waveError && (
                  <Typography variant="caption" color="text.secondary">
                    Loading waveform…
                  </Typography>
                )}
                {waveError && (
                  <Typography variant="caption" color="error">
                    Waveform unavailable ({waveError}). Audio still playable.
                  </Typography>
                )}
              </Box>
              {waveReady && (
                <Typography
                  variant="caption"
                  sx={{
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor: "rgba(15, 23, 42, 0.7)",
                    color: "#fff",
                    fontVariantNumeric: "tabular-nums",
                    fontSize: "0.72rem",
                    letterSpacing: 0.3,
                    pointerEvents: "none",
                  }}
                >
                  {formatTs(currentTime)} /{" "}
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {formatTs(duration)}
                  </Box>
                </Typography>
              )}
            </Box>
          </Box>
          <audio
            ref={hiddenAudioRef}
            src={proxiedAudioUrl(row.recording_file)}
            crossOrigin="anonymous"
            preload="none"
            style={{ display: "none" }}
          />
        </Box>

        {/* Scrollable sentences area */}
        <Box
          sx={{
            maxHeight: "50vh",
            overflowY: "auto",
            pr: 1,
          }}
        >
          {/* Sentence-wise aligned rows */}
          {canonicalLines.map((canonicalLine, lineIndex) => (
            <Grid container spacing={2} key={lineIndex} alignItems="center" sx={{ mb: 1.5 }}>
              {/* Canonical */}
              <Grid item xs={5} sx={{ position: "relative" }}>
                <TextField
                  fullWidth
                  value={canonicalLine}
                  size="small"
                  multiline
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <CopyButton
                        onClick={() => handleCopyToTranscription(lineIndex)}
                        disabled={disabled}
                        title={t(
                          "annotator.annotation.tooltips.copySentence",
                          "Copy this sentence to transcription"
                        )}
                      />
                    ),
                    sx: { pr: 6 }, // Add padding for the copy button
                  }}
                  sx={{ bgcolor: "#f7f8f9" }}
                />
              </Grid>

              {/* Transcription */}
              <Grid item xs={5.5} sx={{ minWidth: 0 }}>
                <TextField
                  fullWidth
                  value={
                    transcriptionLines[lineIndex] === "-" ? "" : transcriptionLines[lineIndex] || ""
                  }
                  size="small"
                  multiline
                  placeholder={`${t("annotator.annotation.placeholders.sentence", "Sentence")} ${
                    lineIndex + 1
                  }`}
                  disabled={disabled}
                  onChange={(e) => {
                    const updated = [...transcriptionLines];
                    updated[lineIndex] = e.target.value;
                    onAnnotationChange(row.recording_id, "transcription_lines", updated);
                  }}
                />
              </Grid>

              {/* Start / End timestamps */}
              <Grid
                item
                xs={1.5}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  minWidth: 0,
                }}
              >
                {/* Start */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "nowrap" }}>
                  {audioTimestamps[lineIndex]?.start != null ? (
                    <>
                      <Button
                        size="small"
                        color="success"
                        variant="outlined"
                        disabled
                        startIcon={<SaveIcon fontSize="small" />}
                        sx={{
                          textTransform: "none",
                          whiteSpace: "nowrap",
                          minWidth: 0,
                          px: 1,
                        }}
                      >
                        {audioTimestamps[lineIndex].start}
                      </Button>
                      <IconButton
                        size="small"
                        onClick={() => handleClearStart(lineIndex)}
                        disabled={disabled}
                        aria-label="clear start timestamp"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={() => handleMarkStart(lineIndex)}
                      disabled={disabled}
                      sx={{
                        textTransform: "none",
                        whiteSpace: "nowrap",
                        minWidth: 0,
                        px: 1.5,
                        color: "#fff",
                      }}
                    >
                      {t("annotator.annotation.buttons.markStart", "Mark start")}
                    </Button>
                  )}
                </Box>

                {/* End */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "nowrap" }}>
                  {audioTimestamps[lineIndex]?.end != null ? (
                    <>
                      <Button
                        size="small"
                        color="success"
                        variant="outlined"
                        disabled
                        startIcon={<SaveIcon fontSize="small" />}
                        sx={{
                          textTransform: "none",
                          whiteSpace: "nowrap",
                          minWidth: 0,
                          px: 1,
                        }}
                      >
                        {audioTimestamps[lineIndex].end}
                      </Button>
                      <IconButton
                        size="small"
                        onClick={() => handleClearEnd(lineIndex)}
                        disabled={disabled}
                        aria-label="clear end timestamp"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={() => handleMarkEnd(lineIndex)}
                      disabled={disabled}
                      sx={{
                        textTransform: "none",
                        whiteSpace: "nowrap",
                        minWidth: 0,
                        px: 1.5,
                        color: "#fff",
                      }}
                    >
                      {t("annotator.annotation.buttons.markEnd", "Mark end")}
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          ))}
        </Box>

        {/* Meta fields */}
        <Grid container spacing={2} mt={2} alignItems="center">
          {/* Read Correctly */}
          <Grid item xs={2.5}>
            <Typography fontSize="0.75rem" fontWeight={600} mb={0.5}>
              {t("annotator.annotation.headers.readCorrectly", "Read Correctly")}
            </Typography>
            <SelectField
              field="read_correctly"
              options={readCorrectOptions}
              defaultValue={t("annotator.annotation.placeholders.select", "Select")}
              selectedValue={row.annotation.read_correctly ? "Yes" : "No"}
              onChange={(e) =>
                onAnnotationChange(row.recording_id, "read_correctly", e.target.value === "Yes")
              }
              disabled={disabled}
            />
          </Grid>

          {/* BG Noise */}
          <Grid item xs={2.5}>
            <Typography fontSize="0.75rem" fontWeight={600} mb={0.5}>
              {t("annotator.annotation.headers.bgNoise", "BG Noise")}
            </Typography>
            <SelectField
              field="bg_noise"
              options={bgNoiseOptions}
              defaultValue={t("annotator.annotation.placeholders.select", "Select")}
              selectedValue={row.annotation.bg_noise || "Select"}
              onChange={(e) => onAnnotationChange(row.recording_id, "bg_noise", e.target.value)}
              disabled={disabled}
            />
          </Grid>

          {/* Non-Transcribable */}
          <Grid item xs={3}>
            <Typography fontSize="0.75rem" fontWeight={600} mb={0.5}>
              {t("annotator.annotation.headers.nonTranscribable", "Non-Transcribable")}
            </Typography>
            <SelectField
              field="not_transcribable"
              options={notTranscribableOptions}
              defaultValue={t("annotator.annotation.placeholders.selectReason", "Select Reason")}
              selectedValue={
                row.annotation.not_transcribable ||
                t("annotator.annotation.placeholders.selectReason", "Select Reason")
              }
              onChange={(e) =>
                onAnnotationChange(row.recording_id, "not_transcribable", e.target.value)
              }
              disabled={disabled}
            />
          </Grid>

          {/* Fluency (passage only, compulsory) */}
          <Grid item xs={2}>
            <Typography fontSize="0.75rem" fontWeight={600} mb={0.5}>
              {t("annotator.annotation.headers.fluency", "Fluency")}
              <span style={{ color: "#d32f2f", marginLeft: 2 }}>*</span>
            </Typography>
            <SelectField
              field="fluency"
              options={fluencyOptions}
              defaultValue={t("annotator.annotation.placeholders.select", "Select")}
              selectedValue={
                row.annotation.fluency === "Yes" || row.annotation.fluency === "No"
                  ? row.annotation.fluency
                  : t("annotator.annotation.placeholders.select", "Select")
              }
              onChange={(e) => onAnnotationChange(row.recording_id, "fluency", e.target.value)}
              disabled={disabled}
            />
          </Grid>

          {/* Save */}
          <Grid item xs={1}>
            <Typography fontSize="0.75rem" fontWeight={600} mb={0.5}>
              &nbsp;
            </Typography>
            <IconButton
              onClick={() => onSaveRecord(row.recording_id)}
              disabled={savingRecord[row.recording_id] || disabled}
            >
              {savingRecord[row.recording_id] ? <CircularProgress size={18} /> : <SaveIcon />}
            </IconButton>
          </Grid>

          {/* View Remark */}
          {row.annotation.remarks && (
            <Grid item xs={2} display="flex" alignItems="center">
              <MDButton
                size="small"
                color="info"
                variant="outlined"
                startIcon={<StickyNote2Icon fontSize="small" />}
                onClick={() => onViewRemark(row.annotation.remarks, row.recording_id)}
                sx={{ whiteSpace: "nowrap", mt: 2.5 }}
              >
                {t("annotator.annotation.buttons.viewRemark", "View Remark")}
              </MDButton>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  }

  /* ================= NON-PASSAGE (ORIGINAL TABLE) ================= */
  /* ================= NON-PASSAGE (TABLE) ================= */
  return (
    <Grid ref={rowRootRef} container spacing={2} py={1} borderBottom="1px solid #f0f0f0">
      {/* Recording (waveform + Play/Pause) */}
      <Grid
        item
        xs={3}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <IconButton
          onClick={togglePlay}
          disabled={disabled}
          color="primary"
          aria-label={isAudioPlaying ? "pause" : "play"}
          sx={{
            border: "1px solid #1976d2",
            "&.Mui-disabled": { borderColor: "#bbb" },
          }}
        >
          {isAudioPlaying ? <PauseIcon /> : <PlayArrowIcon />}
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            ref={waveContainerRef}
            sx={{
              width: "100%",
              minHeight: 60,
              bgcolor: "#fafafa",
              borderRadius: 1,
            }}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 0.5,
              minHeight: 18,
            }}
          >
            <Box sx={{ flex: 1 }}>
              {!waveReady && !waveError && (
                <Typography variant="caption" color="text.secondary">
                  Loading waveform…
                </Typography>
              )}
              {waveError && (
                <Typography variant="caption" color="error">
                  Waveform unavailable. Audio still playable.
                </Typography>
              )}
            </Box>
            {waveReady && (
              <Typography
                variant="caption"
                sx={{
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 1,
                  bgcolor: "rgba(15, 23, 42, 0.7)",
                  color: "#fff",
                  fontVariantNumeric: "tabular-nums",
                  fontSize: "0.72rem",
                  letterSpacing: 0.3,
                  pointerEvents: "none",
                }}
              >
                {formatTs(currentTime)} /{" "}
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {formatTs(duration)}
                </Box>
              </Typography>
            )}
          </Box>
        </Box>
        <audio
          ref={hiddenAudioRef}
          src={proxiedAudioUrl(row.recording_file)}
          crossOrigin="anonymous"
          preload="none"
          style={{ display: "none" }}
        />
      </Grid>

      {/* Canonical */}
      <Grid item xs={1.5}>
        {canonicalLines.map((canonicalLine, lineIndex) => (
          <Box key={lineIndex} sx={{ position: "relative", mb: 0.5 }}>
            <TextField
              value={canonicalLine}
              fullWidth
              size="small"
              multiline
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <CopyButton
                    onClick={() => handleCopyToTranscription(lineIndex)}
                    disabled={disabled}
                    title={t(
                      "annotator.annotation.tooltips.copyLine",
                      "Copy this line to transcription"
                    )}
                  />
                ),
                sx: { pr: 6 }, // Add padding for the copy button
              }}
              sx={{ bgcolor: "#f7f8f9" }}
            />
          </Box>
        ))}
        {/* Copy All Button for non-passage */}
        {canonicalLines.length > 1 && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyAllToTranscription}
            disabled={disabled}
            sx={{ fontSize: "0.75rem", mt: 0.5 }}
            fullWidth
          >
            {t("annotator.annotation.buttons.copyAll", "Copy All")}
          </Button>
        )}
      </Grid>

      {/* Transcription */}
      <Grid item xs={1.5}>
        {transcriptionLines.map((line, lineIndex) => (
          <TextField
            key={lineIndex}
            value={line}
            fullWidth
            size="small"
            multiline
            disabled={disabled}
            sx={{ mb: 0.5 }}
            onChange={(e) => {
              const updated = [...transcriptionLines];
              updated[lineIndex] = e.target.value;
              onAnnotationChange(row.recording_id, "transcription_lines", updated);
            }}
          />
        ))}
      </Grid>

      {/* Start / End timestamps (per canonical line) */}
      <Grid item xs={1.25} sx={{ minWidth: 0 }}>
        {canonicalLines.map((_, lineIndex) => (
          <Box
            key={lineIndex}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              mb: 0.5,
            }}
          >
            {/* Start */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "nowrap" }}>
              {audioTimestamps[lineIndex]?.start != null ? (
                <>
                  <Button
                    size="small"
                    color="success"
                    variant="outlined"
                    disabled
                    startIcon={<SaveIcon fontSize="small" />}
                    sx={{
                      textTransform: "none",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                      px: 1,
                    }}
                  >
                    {audioTimestamps[lineIndex].start}
                  </Button>
                  <IconButton
                    size="small"
                    onClick={() => handleClearStart(lineIndex)}
                    disabled={disabled}
                    aria-label="clear start timestamp"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() => handleMarkStart(lineIndex)}
                  disabled={disabled}
                  sx={{
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                    px: 1.5,
                    color: "#fff",
                  }}
                >
                  {t("annotator.annotation.buttons.markStart", "Mark start")}
                </Button>
              )}
            </Box>

            {/* End */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "nowrap" }}>
              {audioTimestamps[lineIndex]?.end != null ? (
                <>
                  <Button
                    size="small"
                    color="success"
                    variant="outlined"
                    disabled
                    startIcon={<SaveIcon fontSize="small" />}
                    sx={{
                      textTransform: "none",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                      px: 1,
                    }}
                  >
                    {audioTimestamps[lineIndex].end}
                  </Button>
                  <IconButton
                    size="small"
                    onClick={() => handleClearEnd(lineIndex)}
                    disabled={disabled}
                    aria-label="clear end timestamp"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() => handleMarkEnd(lineIndex)}
                  disabled={disabled}
                  sx={{
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                    px: 1.5,
                    color: "#fff",
                  }}
                >
                  {t("annotator.annotation.buttons.markEnd", "Mark end")}
                </Button>
              )}
            </Box>
          </Box>
        ))}
      </Grid>

      {/* Read Correctly */}
      <Grid item xs={1.25}>
        <SelectField
          field="read_correctly"
          options={readCorrectOptions}
          defaultValue={t("annotator.annotation.placeholders.selectCorrect", "Select Correct")}
          selectedValue={row.annotation.read_correctly ? "Yes" : "No"}
          onChange={(e) =>
            onAnnotationChange(row.recording_id, "read_correctly", e.target.value === "Yes")
          }
          disabled={disabled}
        />
      </Grid>

      {/* No. of mistakes (conditional) */}
      {String(row.is_no_mistakes ?? "0") === "1" && (
        <Grid item xs={1}>
          <TextField
            fullWidth
            size="small"
            type="number"
            inputProps={{ min: 0 }}
            value={row.annotation.no_of_mistakes ?? 0}
            disabled={disabled}
            onChange={(e) =>
              onAnnotationChange(
                row.recording_id,
                "no_of_mistakes",
                e.target.value === "" ? "" : Number(e.target.value)
              )
            }
          />
        </Grid>
      )}

      {/* BG Noise */}
      <Grid item xs={1.25}>
        <SelectField
          field="bg_noise"
          options={bgNoiseOptions}
          defaultValue={t("annotator.annotation.placeholders.select", "Select")}
          selectedValue={
            row.annotation.bg_noise || t("annotator.annotation.placeholders.select", "Select")
          }
          onChange={(e) => onAnnotationChange(row.recording_id, "bg_noise", e.target.value)}
          disabled={disabled}
        />
      </Grid>

      {/* Non-Transcribable */}
      <Grid item xs={1.5}>
        <SelectField
          field="not_transcribable"
          options={notTranscribableOptions}
          defaultValue={t("annotator.annotation.placeholders.selectReason", "Select Reason")}
          selectedValue={
            row.annotation.not_transcribable ||
            t("annotator.annotation.placeholders.selectReason", "Select Reason")
          }
          onChange={(e) =>
            onAnnotationChange(row.recording_id, "not_transcribable", e.target.value)
          }
          disabled={disabled}
        />
      </Grid>

      {/* Disfluency Occurences (conditional) */}
      {String(row.is_disfluency ?? "0") === "1" && (
        <Grid item xs={0.75}>
          <TextField
            fullWidth
            size="small"
            type="number"
            inputProps={{ min: 0 }}
            value={row.annotation.disfluency_occurences ?? 0}
            disabled={disabled}
            onChange={(e) =>
              onAnnotationChange(
                row.recording_id,
                "disfluency_occurences",
                e.target.value === "" ? "" : Number(e.target.value)
              )
            }
          />
        </Grid>
      )}

      {/* Save + View Remark */}
      <Grid item xs={0.75}>
        <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
          <IconButton
            onClick={() => onSaveRecord(row.recording_id)}
            disabled={savingRecord[row.recording_id] || disabled}
          >
            {savingRecord[row.recording_id] ? <CircularProgress size={16} /> : <SaveIcon />}
          </IconButton>
          {row.annotation.remarks && (
            <MDButton
              size="small"
              color="info"
              variant="outlined"
              startIcon={<StickyNote2Icon fontSize="small" />}
              onClick={() => onViewRemark(row.annotation.remarks, row.recording_id)}
              sx={{ whiteSpace: "nowrap" }}
            >
              {t("annotator.annotation.buttons.viewRemark", "View Remark")}
            </MDButton>
          )}
        </Box>
      </Grid>
    </Grid>
  );
}

AnnotationRowBase.propTypes = {
  row: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  annotatorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onAnnotationChange: PropTypes.func.isRequired,
  canAnnotate: PropTypes.func.isRequired,
  savingRecord: PropTypes.object.isRequired,
  onSaveRecord: PropTypes.func.isRequired,
  isExamSubmitted: PropTypes.bool.isRequired,
  onViewRemark: PropTypes.func.isRequired,
};

const AnnotationRow = memo(AnnotationRowBase, areRowPropsEqual);

// --- Main Annotation Page Component ---

export default function AnnotationPage() {
  const { t } = useTranslation();
  const query = useQuery();
  const navigate = useNavigate();
  const examId = query.get("exam_id") || "";

  const [loading, setLoading] = useState(false);
  const [exam, setExam] = useState(null);
  const [tabs, setTabs] = useState({});
  const [sampleTypes, setSampleTypes] = useState([]);
  const [activeType, setActiveType] = useState("");
  const [isClearMode, setIsClearMode] = useState(false);

  useEffect(() => {
    setIsClearMode(false);
  }, [activeType]);

  const [confirmCompleteOpen, setConfirmCompleteOpen] = useState(false);
  const [markingComplete, setMarkingComplete] = useState(false);
  const [savingRecord, setSavingRecord] = useState({});
  const [bulkSaving, setBulkSaving] = useState(false);
  const [remarkDialogOpen, setRemarkDialogOpen] = useState(false);
  const [remarkDialogContent, setRemarkDialogContent] = useState("");
  const [remarkDialogRecordingId, setRemarkDialogRecordingId] = useState(null);
  const [resolvingRemark, setResolvingRemark] = useState(false);

  // Navigation state
  const [adjacentExams, setAdjacentExams] = useState({
    next_exam: null,
    prev_exam: null,
    current_exam: null,
    position: 0,
    total: 0,
  });
  const [loadingAdjacent, setLoadingAdjacent] = useState(false);

  // snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    title: "",
    message: "",
  });

  const annotatorId = getLoggedInAnnotatorId();

  const [groupedRecordings, setGroupedRecordings] = useState({});

  const studentId = exam?.student_id || "";
  const language = tabs[sampleTypes[0]]?.[0]?.sample_language || "";
  const getSampleTypeLabel = useCallback(
    (sampleType) => {
      const normalized = String(sampleType || "")
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, "_");
      return t(`annotator.annotation.tabs.${normalized}`, sampleType);
    },
    [t]
  );

  const myExamStatus = useMemo(() => {
    if (!exam || !annotatorId) return "";

    const isA1 = String(exam.annotator1_id ?? "") === String(annotatorId);
    const isA2 = String(exam.annotator2_id ?? "") === String(annotatorId);

    if (isA1) return String(exam.annotator1_status || "").toLowerCase();
    if (isA2) return String(exam.annotator2_status || "").toLowerCase();

    return "";
  }, [exam, annotatorId]);

  const isMyExamCompleted = myExamStatus === "submitted" || myExamStatus === "completed";
  const isExamSubmitted = isMyExamCompleted;

  // snackbar helpers
  const showSnackbar = useCallback(
    ({ severity = "success", title = "", message = "", duration = 5000 }) => {
      setSnackbar({ open: true, severity, title, message });
      if (duration > 0) {
        setTimeout(() => setSnackbar((s) => ({ ...s, open: false })), duration);
      }
    },
    []
  );

  const closeSnackbar = useCallback(() => setSnackbar((s) => ({ ...s, open: false })), []);

  const handleViewRemark = useCallback((remarkText, recordingId) => {
    setRemarkDialogContent(remarkText || "");
    setRemarkDialogRecordingId(recordingId ?? null);
    setRemarkDialogOpen(true);
  }, []);

  const handleResolveRemark = useCallback(async () => {
    if (!remarkDialogRecordingId || !annotatorId) return;
    setResolvingRemark(true);
    try {
      await resolveRemark(remarkDialogRecordingId, annotatorId);
      // Blank the remark locally so the "View Remark" button disappears
      setGroupedRecordings((prev) => {
        const next = { ...prev };
        for (const tab of Object.keys(next)) {
          next[tab] = (next[tab] || []).map((rec) =>
            String(rec.recording_id) === String(remarkDialogRecordingId)
              ? { ...rec, annotation: { ...rec.annotation, remarks: "" } }
              : rec
          );
        }
        return next;
      });
      setRemarkDialogOpen(false);
      setRemarkDialogRecordingId(null);
      showSnackbar({
        severity: "success",
        title: t("annotator.annotation.snackbar.remarkResolvedTitle", "Remark Resolved"),
        message: t(
          "annotator.annotation.snackbar.remarkResolvedMessage",
          "Remark resolved successfully"
        ),
      });
    } catch (err) {
      showSnackbar({
        severity: "error",
        title: t("annotator.annotation.snackbar.remarkResolveFailedTitle", "Resolve Failed"),
        message:
          err?.message ||
          t("annotator.annotation.snackbar.remarkResolveFailedMessage", "Failed to resolve remark"),
      });
    } finally {
      setResolvingRemark(false);
    }
  }, [remarkDialogRecordingId, annotatorId, showSnackbar, t]);

  // Helper to check if annotator assigned
  const canAnnotate = useCallback(
    (rec) => {
      if (!rec) return false;
      const isA1 = String(rec.annotator1_id ?? "") === String(annotatorId);
      const isA2 = String(rec.annotator2_id ?? "") === String(annotatorId);
      return isA1 || isA2;
    },
    [annotatorId]
  );

  // Flatten and init annotations
  const processExamBundle = useCallback(
    async (bundle) => {
      const newGroupedRecordings = {};

      // Fetch all existing annotations for this exam in ONE request instead of
      // one request per recording. Falls back to per-recording getAnnotation
      // if the bulk endpoint is unavailable (e.g. older backend).
      let annotationsById = null;
      const bundleExamId = bundle?.exam?.exam_id;
      if (bundleExamId && annotatorId) {
        try {
          const bulkResp = await getAnnotationsBulk(bundleExamId, annotatorId);
          if (bulkResp?.status === "success" && Array.isArray(bulkResp.data)) {
            annotationsById = new Map(bulkResp.data.map((a) => [String(a.recording_id), a]));
          }
        } catch (error) {
          console.error("getAnnotationsBulk failed, falling back to per-recording fetch:", error);
        }
      }

      for (const sampleType of bundle.sample_types) {
        const currentGroups = bundle.tabs[sampleType] || [];
        const recordingsWithSampleText = currentGroups.flatMap((group) =>
          group.recordings.map((rec) => {
            const isPassage = rec.is_passage === 1 || rec.is_passage === "1";
            const sampleTextLines = splitTextIntoLines(group.sample_text, isPassage);

            return {
              ...rec,
              is_disfluency: rec.is_disfluency ?? group.is_disfluency ?? 0,
              is_no_mistakes: rec.is_no_mistakes ?? group.is_no_mistakes ?? 1,
              is_passage: rec.is_passage ?? group.is_passage ?? 0,
              sample_text: group.sample_text,
              sample_type: sampleType,
              annotation: {
                transcription_text: "",
                transcription_lines: sampleTextLines.map(() => ""),
                audio_timestamps: sampleTextLines.map(() => ({ start: null, end: null })),
                read_correctly: false,
                bg_noise: "",
                not_transcribable: "",
                disfluency_occurences: 0,
                no_of_mistakes: 0,
                fluency: "",
              },
            };
          })
        );

        const annotationPromises = recordingsWithSampleText.map(async (rec) => {
          try {
            // Bulk map lookup when available; per-recording fetch as fallback.
            const existingAnnotation = annotationsById
              ? { status: "success", data: annotationsById.get(String(rec.recording_id)) || null }
              : await getAnnotation(rec.recording_id, annotatorId);
            if (existingAnnotation?.status === "success" && existingAnnotation.data) {
              const data = existingAnnotation.data;
              const isPassage = rec.is_passage === 1 || rec.is_passage === "1";

              const canonicalTextLines = splitTextIntoLines(rec.sample_text, isPassage);
              const canonicalLineCount = canonicalTextLines.length;

              let transcriptionLines;

              if (data.transcription_text && data.transcription_text.trim() !== "") {
                transcriptionLines = data.transcription_text.split("\n");
                transcriptionLines = transcriptionLines.map((line) => line.trim());
              } else {
                transcriptionLines = Array(canonicalLineCount).fill("");
              }

              if (transcriptionLines.length < canonicalLineCount) {
                const missingLines = Array(canonicalLineCount - transcriptionLines.length).fill("");
                transcriptionLines = [...transcriptionLines, ...missingLines];
              } else if (transcriptionLines.length > canonicalLineCount) {
                transcriptionLines = transcriptionLines.slice(0, canonicalLineCount);
              }

              let audioTimestamps = [];
              if (data.audio_timestamps) {
                try {
                  const parsed =
                    typeof data.audio_timestamps === "string"
                      ? JSON.parse(data.audio_timestamps)
                      : data.audio_timestamps;
                  audioTimestamps = Array.isArray(parsed)
                    ? parsed.map((it) => {
                        if (!it || typeof it !== "object") {
                          return { start: null, end: null };
                        }
                        return {
                          start:
                            it.start_timestamp ??
                            it.mark_start_timestamp ??
                            (it.mark_end_timestamp && typeof it.mark_end_timestamp === "object"
                              ? it.mark_end_timestamp.start
                              : null) ??
                            null,
                          end:
                            it.end_timestamp ??
                            (it.mark_end_timestamp && typeof it.mark_end_timestamp === "object"
                              ? it.mark_end_timestamp.end
                              : typeof it.mark_end_timestamp === "string"
                              ? it.mark_end_timestamp
                              : null) ??
                            null,
                        };
                      })
                    : [];
                } catch (e) {
                  audioTimestamps = [];
                }
              }
              if (audioTimestamps.length < canonicalLineCount) {
                audioTimestamps = [
                  ...audioTimestamps,
                  ...Array.from({ length: canonicalLineCount - audioTimestamps.length }, () => ({
                    start: null,
                    end: null,
                  })),
                ];
              } else if (audioTimestamps.length > canonicalLineCount) {
                audioTimestamps = audioTimestamps.slice(0, canonicalLineCount);
              }

              const fluencyFromDb =
                data.fluency === 1 || data.fluency === "1"
                  ? "Yes"
                  : data.fluency === 0 || data.fluency === "0"
                  ? "No"
                  : "";

              return {
                ...rec,
                annotation: {
                  transcription_text: data.transcription_text || "",
                  transcription_lines: transcriptionLines,
                  audio_timestamps: audioTimestamps,
                  read_correctly: Boolean(data.read_correctly),
                  bg_noise: data.bg_noise ?? "",
                  not_transcribable: data.not_transcribable ?? "",
                  disfluency_occurences:
                    data.disfluency_occurences ?? rec.annotation.disfluency_occurences ?? 0,
                  no_of_mistakes: data.no_of_mistakes ?? rec.annotation.no_of_mistakes ?? 0,
                  fluency: fluencyFromDb,
                  remarks: data.remarks ?? "",
                },
              };
            }
          } catch (error) {
            console.error(`Failed to fetch annotation for ${rec.recording_id}:`, error);
          }
          return rec;
        });

        newGroupedRecordings[sampleType] = await Promise.all(annotationPromises);
      }

      setGroupedRecordings(newGroupedRecordings);
    },
    [annotatorId]
  );

  // Function to get adjacent exams
  const fetchAdjacentExams = useCallback(async () => {
    if (!examId || !annotatorId) return;

    try {
      setLoadingAdjacent(true);
      // Import the function dynamically to avoid circular dependency
      const { getAdjacentExams } = await import("services/sampleService");
      const navigationFilters = {
        status: "In Progress", // or any other filter you want
      };
      const data = await getAdjacentExams(examId, annotatorId, navigationFilters);
      setAdjacentExams(data);
    } catch (error) {
      console.error("Failed to fetch adjacent exams:", error);
      // Don't show error snackbar for navigation data - it's optional
    } finally {
      setLoadingAdjacent(false);
    }
  }, [examId, annotatorId]);

  // Function to navigate to another exam
  const handleNavigateToExam = useCallback(
    (newExamId) => {
      if (!newExamId || newExamId === examId) return;

      const newSearchParams = new URLSearchParams(query.toString());
      newSearchParams.set("exam_id", newExamId);

      navigate(
        {
          pathname: window.location.pathname,
          search: newSearchParams.toString(),
        },
        { replace: true }
      );
    },
    [examId, navigate, query]
  );

  const handlePreviousExam = useCallback(() => {
    if (adjacentExams.prev_exam?.exam_id) {
      handleNavigateToExam(adjacentExams.prev_exam.exam_id);
    }
  }, [adjacentExams.prev_exam, handleNavigateToExam]);

  const handleNextExam = useCallback(() => {
    if (adjacentExams.next_exam?.exam_id) {
      handleNavigateToExam(adjacentExams.next_exam.exam_id);
    }
  }, [adjacentExams.next_exam, handleNavigateToExam]);

  // Add keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") {
        return;
      }

      if (e.ctrlKey && e.key === "ArrowLeft") {
        e.preventDefault();
        handlePreviousExam();
      }

      if (e.ctrlKey && e.key === "ArrowRight") {
        e.preventDefault();
        handleNextExam();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handlePreviousExam, handleNextExam]);

  // initial fetch for exam data
  useEffect(() => {
    if (!examId) return;
    (async () => {
      try {
        setLoading(true);
        const bundle = await getExamBundle(examId);
        setExam(bundle.exam || null);
        setTabs(bundle.tabs || {});
        setSampleTypes(bundle.sample_types || []);
        setActiveType(bundle.sample_types?.[0] || "");
        await processExamBundle(bundle);
      } catch (err) {
        console.error("getExamBundle failed:", err);
        showSnackbar({
          severity: "error",
          title: t("annotator.annotation.snackbar.loadFailedTitle", "Load failed"),
          message: t("annotator.annotation.snackbar.loadFailedMessage", "Failed to load exam data"),
        });
        setExam(null);
        setTabs({});
        setSampleTypes([]);
        setActiveType("");
        setGroupedRecordings({});
      } finally {
        setLoading(false);
      }
    })();
  }, [examId, processExamBundle, showSnackbar, t]);

  // Fetch adjacent exams when examId or annotatorId changes
  useEffect(() => {
    fetchAdjacentExams();
  }, [examId, annotatorId, fetchAdjacentExams]);

  const activeRecordings = useMemo(
    () => groupedRecordings[activeType] || [],
    [groupedRecordings, activeType]
  );

  const hasAnyPassage = useMemo(
    () => activeRecordings.some((r) => r?.is_passage === 1 || r?.is_passage === "1"),
    [activeRecordings]
  );

  const hasDisfluency = useMemo(
    () => activeRecordings.some((r) => String(r?.is_disfluency ?? 0) === "1"),
    [activeRecordings]
  );
  const hasNoMistakes = useMemo(
    () => activeRecordings.some((r) => String(r?.is_no_mistakes ?? 0) === "1"),
    [activeRecordings]
  );

  const getSectionStatus = (sampleType) => {
    const recs = groupedRecordings[sampleType] || [];
    if (!recs.length) return "pending";
    const lowerStatus = (s) => String(s || "").toLowerCase();
    const allBothSubmitted = recs.every(
      (r) =>
        lowerStatus(r.annotation1_status) === "submitted" &&
        lowerStatus(r.annotation2_status) === "submitted"
    );
    if (allBothSubmitted) return "completed";
    const anySubmitted = recs.some(
      (r) =>
        lowerStatus(r.annotation1_status) === "submitted" ||
        lowerStatus(r.annotation2_status) === "submitted"
    );
    if (anySubmitted) return "inProgress";
    return "pending";
  };

  // Per-current-annotator save progress for a section
  const getMySectionProgress = (sampleType) => {
    const recs = groupedRecordings[sampleType] || [];
    const total = recs.length;
    if (!total) return { status: "pending", saved: 0, total: 0 };
    const lowerStatus = (s) => String(s || "").toLowerCase();
    const saved = recs.reduce((acc, rec) => {
      const isA1 = String(rec.annotator1_id ?? "") === String(annotatorId);
      const isA2 = String(rec.annotator2_id ?? "") === String(annotatorId);
      const myStatus = isA1 ? rec.annotation1_status : isA2 ? rec.annotation2_status : null;
      return lowerStatus(myStatus) === "submitted" ? acc + 1 : acc;
    }, 0);
    let status = "pending";
    if (saved === total) status = "completed";
    else if (saved > 0) status = "inProgress";
    return { status, saved, total };
  };

  // annotation change for a recording
  const handleAnnotationChange = useCallback(
    (recordingId, field, value) => {
      if (isExamSubmitted) return;

      setGroupedRecordings((prev) => {
        const newGrouped = { ...prev };
        const updatedRecordings = newGrouped[activeType].map((rec) => {
          if (rec.recording_id === recordingId) {
            if (field === "transcription_lines") {
              const lines = Array.isArray(value) ? value : [];
              const concatenatedText = joinLinesIntoText(lines);
              return {
                ...rec,
                annotation: {
                  ...rec.annotation,
                  transcription_lines: lines,
                  transcription_text: concatenatedText,
                },
              };
            }
            return { ...rec, annotation: { ...rec.annotation, [field]: value } };
          }
          return rec;
        });
        newGrouped[activeType] = updatedRecordings;
        return newGrouped;
      });
    },
    [activeType, isExamSubmitted]
  );

  const handlePreviousTab = () => {
    const currentIndex = sampleTypes.indexOf(activeType);
    if (currentIndex > 0) setActiveType(sampleTypes[currentIndex - 1]);
  };

  const handleNextTab = () => {
    const currentIndex = sampleTypes.indexOf(activeType);
    if (currentIndex < sampleTypes.length - 1) setActiveType(sampleTypes[currentIndex + 1]);
  };

  // Patch a single recording's annotation in groupedRecordings from the server-returned row.
  // Mirrors what processExamBundle writes per recording, so we can skip the post-save re-fetch.
  const applyAnnotationToState = useCallback(
    (savedRow) => {
      if (!savedRow || !savedRow.recording_id) return;
      setGroupedRecordings((prev) => {
        const next = { ...prev };
        for (const tab of Object.keys(next)) {
          const list = next[tab] || [];
          const idx = list.findIndex((r) => r.recording_id === savedRow.recording_id);
          if (idx === -1) continue;

          const rec = list[idx];
          const isPassage = rec.is_passage === 1 || rec.is_passage === "1";
          const canonicalCount = splitTextIntoLines(rec.sample_text, isPassage).length;

          // transcription_lines
          let transcriptionLines = rec.annotation?.transcription_lines || [];
          if (savedRow.transcription_text) {
            transcriptionLines = String(savedRow.transcription_text)
              .split("\n")
              .map((s) => s.trim());
          }
          if (transcriptionLines.length < canonicalCount) {
            transcriptionLines = [
              ...transcriptionLines,
              ...Array(canonicalCount - transcriptionLines.length).fill(""),
            ];
          } else if (transcriptionLines.length > canonicalCount) {
            transcriptionLines = transcriptionLines.slice(0, canonicalCount);
          }

          // audio_timestamps — parse with backward-compat fallback chain
          let audioTimestamps = rec.annotation?.audio_timestamps || [];
          if (savedRow.audio_timestamps != null) {
            try {
              const parsed =
                typeof savedRow.audio_timestamps === "string"
                  ? JSON.parse(savedRow.audio_timestamps)
                  : savedRow.audio_timestamps;
              audioTimestamps = Array.isArray(parsed)
                ? parsed.map((it) => {
                    if (!it || typeof it !== "object") return { start: null, end: null };
                    return {
                      start:
                        it.start_timestamp ??
                        it.mark_start_timestamp ??
                        (it.mark_end_timestamp && typeof it.mark_end_timestamp === "object"
                          ? it.mark_end_timestamp.start
                          : null) ??
                        null,
                      end:
                        it.end_timestamp ??
                        (it.mark_end_timestamp && typeof it.mark_end_timestamp === "object"
                          ? it.mark_end_timestamp.end
                          : typeof it.mark_end_timestamp === "string"
                          ? it.mark_end_timestamp
                          : null) ??
                        null,
                    };
                  })
                : [];
            } catch (e) {
              // keep existing on parse error
            }
          }
          if (audioTimestamps.length < canonicalCount) {
            audioTimestamps = [
              ...audioTimestamps,
              ...Array.from({ length: canonicalCount - audioTimestamps.length }, () => ({
                start: null,
                end: null,
              })),
            ];
          } else if (audioTimestamps.length > canonicalCount) {
            audioTimestamps = audioTimestamps.slice(0, canonicalCount);
          }

          const fluencyFromDb =
            savedRow.fluency === 1 || savedRow.fluency === "1"
              ? "Yes"
              : savedRow.fluency === 0 || savedRow.fluency === "0"
              ? "No"
              : "";

          const isA1 = String(rec.annotator1_id ?? "") === String(annotatorId);
          const isA2 = String(rec.annotator2_id ?? "") === String(annotatorId);

          const updatedRec = {
            ...rec,
            annotation: {
              ...rec.annotation,
              transcription_text: savedRow.transcription_text || "",
              transcription_lines: transcriptionLines,
              audio_timestamps: audioTimestamps,
              read_correctly: Boolean(savedRow.read_correctly),
              bg_noise: savedRow.bg_noise ?? "",
              not_transcribable: savedRow.not_transcribable ?? "",
              disfluency_occurences:
                savedRow.disfluency_occurences ?? rec.annotation?.disfluency_occurences ?? 0,
              no_of_mistakes: savedRow.no_of_mistakes ?? rec.annotation?.no_of_mistakes ?? 0,
              fluency: fluencyFromDb || rec.annotation?.fluency || "",
              remarks: savedRow.remarks ?? rec.annotation?.remarks ?? "",
            },
            ...(isA1 ? { annotation1_status: "Submitted" } : {}),
            ...(isA2 ? { annotation2_status: "Submitted" } : {}),
          };

          const newList = [...list];
          newList[idx] = updatedRec;
          next[tab] = newList;
          break;
        }
        return next;
      });
    },
    [annotatorId]
  );

  // Validate one recording before save.
  //
  // Letter / Word / Non-word — mutually exclusive paths:
  //   Option A — Transcription done: every line has transcript + start + end.
  //              Non-Transcribable must be empty.
  //   Option B — Cannot transcribe: Non-Transcribable selected. Every line's
  //              transcript / start / end must be empty.
  //
  // Passage — more lenient:
  //   - If EVERYTHING is empty (no transcripts, no timestamps anywhere) →
  //     Non-Transcribable must be selected.
  //   - If anything is filled → Non-Transcribable is NOT required;
  //     partial fills are allowed.
  const validateRecordingBeforeSave = (recording) => {
    const notTranscribableRaw = recording?.annotation?.not_transcribable;
    const notTranscribable = String(notTranscribableRaw || "").trim();
    const hasNonTranscribable = notTranscribable && notTranscribable !== "Select Reason";

    const isPassage = recording?.is_passage === 1 || recording?.is_passage === "1";
    const canonical = splitTextIntoLines(recording?.sample_text || "", isPassage);
    const transcriptionLines = recording?.annotation?.transcription_lines || [];
    const audioTimestamps = recording?.annotation?.audio_timestamps || [];

    let everyLineFullyFilled = true;
    let everyLineFullyEmpty = true;

    canonical.forEach((_, i) => {
      const transcript = String(transcriptionLines[i] || "").trim();
      const start = audioTimestamps[i]?.start;
      const end = audioTimestamps[i]?.end;

      const tFilled = transcript !== "" && transcript !== "-";
      const sFilled = start != null && start !== "";
      const eFilled = end != null && end !== "";

      if (!(tFilled && sFilled && eFilled)) everyLineFullyFilled = false;
      if (tFilled || sFilled || eFilled) everyLineFullyEmpty = false;
    });

    if (isPassage) {
      // Non-Transcribable is only allowed when EVERY sentence is blank.
      if (hasNonTranscribable && !everyLineFullyEmpty) {
        return {
          ok: false,
          reason: "passage_partial_with_nt",
          message:
            "Non-Transcribable is only allowed when all sentences are blank. Clear all transcripts and Mark Start/End, or unselect Non-Transcribable.",
        };
      }
      // Block when nothing is annotated AND Non-Transcribable is not picked.
      if (everyLineFullyEmpty && !hasNonTranscribable) {
        return {
          ok: false,
          reason: "passage_all_empty",
          message:
            "Fill transcript / Mark Start/End for at least one sentence, or select a Non-Transcribable reason.",
        };
      }
      return { ok: true };
    }

    // Non-passage rules (Letter / Word / Non-word)
    if (hasNonTranscribable) {
      if (everyLineFullyEmpty) return { ok: true };
      return {
        ok: false,
        reason: "B_violation",
        message:
          "Non-Transcribable is selected — clear transcripts and Mark Start/End timestamps, or unselect Non-Transcribable and fill them in completely.",
      };
    }

    if (everyLineFullyFilled) return { ok: true };
    return {
      ok: false,
      reason: "A_violation",
      message:
        "Fill transcript and Mark Start/End for every line, or pick a Non-Transcribable reason (and leave them all empty).",
    };
  };

  // Save a single record
  const handleSaveRecord = async (recordingId) => {
    if (!annotatorId) {
      showSnackbar({
        severity: "error",
        title: t("annotator.annotation.snackbar.notLoggedInTitle", "Not logged in"),
        message: t("annotator.annotation.snackbar.notLoggedInMessage", "Annotator not found"),
      });
      return;
    }

    if (isExamSubmitted) {
      showSnackbar({
        severity: "error",
        title: t("annotator.annotation.snackbar.examSubmittedTitle", "Exam Submitted"),
        message: t(
          "annotator.annotation.snackbar.examAlreadySubmittedMessage",
          "Cannot save - exam is already submitted"
        ),
      });
      return;
    }

    const recording = activeRecordings.find((rec) => rec.recording_id === recordingId);
    const isPassageRow = recording?.is_passage === 1 || recording?.is_passage === "1";
    if (
      isPassageRow &&
      recording.annotation.fluency !== "Yes" &&
      recording.annotation.fluency !== "No"
    ) {
      showSnackbar({
        severity: "error",
        title: t("annotator.annotation.snackbar.fluencyRequiredTitle", "Fluency Required"),
        message: t(
          "annotator.annotation.snackbar.fluencyRequiredMessage",
          "Please select Fluency (Yes/No) before saving the passage."
        ),
      });
      return;
    }

    const validation = validateRecordingBeforeSave(recording);
    if (!validation.ok) {
      showSnackbar({
        severity: "error",
        title: t("annotator.annotation.snackbar.transcriptRequiredTitle", "Invalid entry"),
        message: t(`annotator.annotation.snackbar.${validation.reason}`, validation.message),
      });
      return;
    }

    setSavingRecord((prev) => ({ ...prev, [recordingId]: true }));

    try {
      if (!recording || !canAnnotate(recording)) {
        throw new Error("Cannot save - recording not found or not assigned to you");
      }

      const transcriptionLines =
        recording.annotation.transcription_lines ||
        splitTextIntoLines(recording.annotation.transcription_text || "");

      const lines = Array.isArray(transcriptionLines) ? transcriptionLines : [""];
      const concatenatedText = joinLinesIntoText(lines);

      const canonical = splitTextIntoLines(recording.sample_text, isPassageRow);
      const tsArr = recording.annotation.audio_timestamps || [];
      const audioTimestampsPayload = canonical.map((sentence, i) => ({
        text: sentence,
        transcript: lines[i] || "",
        start_timestamp: tsArr[i]?.start ?? null,
        end_timestamp: tsArr[i]?.end ?? null,
      }));

      const annotationData = {
        recording_id: recordingId,
        annotator_id: annotatorId,
        transcription_text: concatenatedText,
        read_correctly: recording.annotation.read_correctly,
        bg_noise: recording.annotation.bg_noise,
        not_transcribable: recording.annotation.not_transcribable,
        round: 1,
        no_of_mistakes:
          recording.annotation.no_of_mistakes === "" ||
          recording.annotation.no_of_mistakes === undefined
            ? 0
            : recording.annotation.no_of_mistakes,
        disfluency_occurences:
          recording.annotation.disfluency_occurences === "" ||
          recording.annotation.disfluency_occurences === undefined
            ? 0
            : recording.annotation.disfluency_occurences,
        fluency: isPassageRow ? recording.annotation.fluency : null,
        audio_timestamps: audioTimestampsPayload,
      };

      const result = await saveAnnotation(annotationData);
      if (result.status !== "success") {
        throw new Error(result.message || "Failed to save annotation");
      }

      showSnackbar({
        severity: "success",
        title: t("annotator.annotation.snackbar.savedTitle", "Saved"),
        message: t("annotator.annotation.snackbar.savedMessage", "Annotation saved successfully"),
        duration: 2500,
      });

      applyAnnotationToState(result.data);

      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Failed to save record:", error);
      showSnackbar({
        severity: "error",
        title: t("annotator.annotation.snackbar.saveFailedTitle", "Save failed"),
        message:
          error.message ||
          t("annotator.annotation.snackbar.saveFailedMessage", "Failed to save annotation"),
      });
    } finally {
      setSavingRecord((prev) => ({ ...prev, [recordingId]: false }));
    }
  };

  const handleSaveCurrentTab = async () => {
    if (!annotatorId || !activeType) {
      showSnackbar({
        severity: "error",
        title: t("annotator.annotation.snackbar.cannotSaveTitle", "Cannot Save"),
        message: t(
          "annotator.annotation.snackbar.missingAnnotatorOrTabMessage",
          "Missing annotator ID or active tab"
        ),
      });
      return;
    }

    if (isExamSubmitted) {
      showSnackbar({
        severity: "error",
        title: t("annotator.annotation.snackbar.examSubmittedTitle", "Exam Submitted"),
        message: t(
          "annotator.annotation.snackbar.examAlreadySubmittedMessage",
          "Cannot save - exam is already submitted"
        ),
      });
      return;
    }

    const recordingsToSavePre = activeRecordings.filter((rec) => canAnnotate(rec));
    const missingFluency = recordingsToSavePre.filter((rec) => {
      const isPassageRow = rec?.is_passage === 1 || rec?.is_passage === "1";
      return isPassageRow && rec.annotation.fluency !== "Yes" && rec.annotation.fluency !== "No";
    });
    if (missingFluency.length > 0) {
      showSnackbar({
        severity: "error",
        title: t("annotator.annotation.snackbar.fluencyRequiredTitle", "Fluency Required"),
        message: t(
          "annotator.annotation.snackbar.fluencyRequiredBulkMessage",
          "Please select Fluency (Yes/No) for all passage recordings before saving."
        ),
      });
      return;
    }

    const invalidRecordings = recordingsToSavePre
      .map((rec, i) => ({ rec, index: i, result: validateRecordingBeforeSave(rec) }))
      .filter((x) => !x.result.ok);
    if (invalidRecordings.length > 0) {
      const total = invalidRecordings.length;
      const firstRowIdx = invalidRecordings[0].index + 1;
      showSnackbar({
        severity: "error",
        title: t("annotator.annotation.snackbar.transcriptRequiredBulkTitle", "Invalid entries"),
        message: t(
          "annotator.annotation.snackbar.transcriptRequiredBulkMessage",
          `${total} row(s) invalid: either fill transcript + Mark Start/End on every line, OR pick Non-Transcribable and leave them all empty. First invalid row: #${firstRowIdx}.`
        ),
      });
      return;
    }

    setBulkSaving(true);

    try {
      const recordingsToSave = recordingsToSavePre;

      if (recordingsToSave.length === 0) {
        showSnackbar({
          severity: "info",
          title: t("annotator.annotation.snackbar.noChangesTitle", "No Changes"),
          message: t(
            "annotator.annotation.snackbar.noRecordingsToSaveMessage",
            "No recordings to save in this tab"
          ),
        });
        return;
      }

      const annotationsData = recordingsToSave.map((recording) => {
        const transcriptionLines =
          recording.annotation.transcription_lines ||
          splitTextIntoLines(recording.annotation.transcription_text || "");

        const lines = Array.isArray(transcriptionLines) ? transcriptionLines : [""];
        const concatenatedText = joinLinesIntoText(lines);

        const isPassageRow = recording?.is_passage === 1 || recording?.is_passage === "1";

        const canonical = splitTextIntoLines(recording.sample_text, isPassageRow);
        const tsArr = recording.annotation.audio_timestamps || [];
        const audioTimestampsPayload = canonical.map((sentence, i) => ({
          text: sentence,
          transcript: lines[i] || "",
          start_timestamp: tsArr[i]?.start ?? null,
          end_timestamp: tsArr[i]?.end ?? null,
        }));

        return {
          recording_id: recording.recording_id,
          annotator_id: annotatorId,
          transcription_text: concatenatedText || "",
          read_correctly: recording.annotation.read_correctly || false,
          bg_noise: recording.annotation.bg_noise || "",
          not_transcribable: recording.annotation.not_transcribable || "",
          round: 1,
          no_of_mistakes:
            recording.annotation.no_of_mistakes === "" ||
            recording.annotation.no_of_mistakes === undefined
              ? 0
              : recording.annotation.no_of_mistakes,
          disfluency_occurences:
            recording.annotation.disfluency_occurences === "" ||
            recording.annotation.disfluency_occurences === undefined
              ? 0
              : recording.annotation.disfluency_occurences,
          fluency: isPassageRow ? recording.annotation.fluency : null,
          audio_timestamps: audioTimestampsPayload,
        };
      });

      const result = await saveAnnotationsBulk(annotationsData);

      if (result.status !== "success" && result.status !== "partial_success") {
        throw new Error(result.message || "Failed to save annotations");
      }

      showSnackbar({
        severity: result.status === "success" ? "success" : "warning",
        title:
          result.status === "success"
            ? t("annotator.annotation.snackbar.savedTitle", "Saved")
            : t("annotator.annotation.snackbar.partiallySavedTitle", "Partially Saved"),
        message: result.message || `Saved ${result.summary?.successful || 0} recording(s)`,
        duration: 3000,
      });

      if (Array.isArray(result.data)) result.data.forEach(applyAnnotationToState);

      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Failed to save annotations:", error);
      showSnackbar({
        severity: "error",
        title: t("annotator.annotation.snackbar.saveFailedTitle", "Save Failed"),
        message:
          error.message ||
          t(
            "annotator.annotation.snackbar.saveBulkFailedMessage",
            "Failed to save annotations. Please try again."
          ),
      });
    } finally {
      setBulkSaving(false);
    }
  };

  const handleSaveAndNext = async () => {
    if (!annotatorId || !activeType) {
      showSnackbar({
        severity: "error",
        title: t("annotator.annotation.snackbar.cannotSaveTitle", "Cannot Save"),
        message: t(
          "annotator.annotation.snackbar.missingAnnotatorOrTabMessage",
          "Missing annotator ID or active tab"
        ),
      });
      return;
    }

    if (isExamSubmitted) {
      showSnackbar({
        severity: "error",
        title: t("annotator.annotation.snackbar.examSubmittedTitle", "Exam Submitted"),
        message: t(
          "annotator.annotation.snackbar.examAlreadySubmittedMessage",
          "Cannot save - exam is already submitted"
        ),
      });
      return;
    }

    const recordingsToSavePre = activeRecordings.filter((rec) => canAnnotate(rec));
    const missingFluency = recordingsToSavePre.filter((rec) => {
      const isPassageRow = rec?.is_passage === 1 || rec?.is_passage === "1";
      return isPassageRow && rec.annotation.fluency !== "Yes" && rec.annotation.fluency !== "No";
    });
    if (missingFluency.length > 0) {
      showSnackbar({
        severity: "error",
        title: t("annotator.annotation.snackbar.fluencyRequiredTitle", "Fluency Required"),
        message: t(
          "annotator.annotation.snackbar.fluencyRequiredBulkMessage",
          "Please select Fluency (Yes/No) for all passage recordings before saving."
        ),
      });
      return;
    }

    const invalidRecordings = recordingsToSavePre
      .map((rec, i) => ({ rec, index: i, result: validateRecordingBeforeSave(rec) }))
      .filter((x) => !x.result.ok);
    if (invalidRecordings.length > 0) {
      const total = invalidRecordings.length;
      const firstRowIdx = invalidRecordings[0].index + 1;
      showSnackbar({
        severity: "error",
        title: t("annotator.annotation.snackbar.transcriptRequiredBulkTitle", "Invalid entries"),
        message: t(
          "annotator.annotation.snackbar.transcriptRequiredBulkMessage",
          `${total} row(s) invalid: either fill transcript + Mark Start/End on every line, OR pick Non-Transcribable and leave them all empty. First invalid row: #${firstRowIdx}.`
        ),
      });
      return;
    }

    setBulkSaving(true);

    try {
      const recordingsToSave = recordingsToSavePre;

      if (recordingsToSave.length === 0) {
        showSnackbar({
          severity: "info",
          title: t("annotator.annotation.snackbar.noChangesTitle", "No Changes"),
          message: t(
            "annotator.annotation.snackbar.noRecordingsToSaveMessage",
            "No recordings to save in this tab"
          ),
        });
        handleNextTab();
        return;
      }

      const annotationsData = recordingsToSave.map((recording) => {
        const transcriptionLines =
          recording.annotation.transcription_lines ||
          splitTextIntoLines(recording.annotation.transcription_text || "");

        const lines = Array.isArray(transcriptionLines) ? transcriptionLines : [""];
        const concatenatedText = joinLinesIntoText(lines);

        const isPassageRow = recording?.is_passage === 1 || recording?.is_passage === "1";

        const canonical = splitTextIntoLines(recording.sample_text, isPassageRow);
        const tsArr = recording.annotation.audio_timestamps || [];
        const audioTimestampsPayload = canonical.map((sentence, i) => ({
          text: sentence,
          transcript: lines[i] || "",
          start_timestamp: tsArr[i]?.start ?? null,
          end_timestamp: tsArr[i]?.end ?? null,
        }));

        return {
          recording_id: recording.recording_id,
          annotator_id: annotatorId,
          transcription_text: concatenatedText || "",
          read_correctly: recording.annotation.read_correctly || false,
          bg_noise: recording.annotation.bg_noise || "",
          not_transcribable: recording.annotation.not_transcribable || "",
          round: 1,
          no_of_mistakes:
            recording.annotation.no_of_mistakes === "" ||
            recording.annotation.no_of_mistakes === undefined
              ? 0
              : recording.annotation.no_of_mistakes,
          disfluency_occurences:
            recording.annotation.disfluency_occurences === "" ||
            recording.annotation.disfluency_occurences === undefined
              ? 0
              : recording.annotation.disfluency_occurences,
          fluency: isPassageRow ? recording.annotation.fluency : null,
          audio_timestamps: audioTimestampsPayload,
        };
      });

      const result = await saveAnnotationsBulk(annotationsData);

      if (result.status !== "success" && result.status !== "partial_success") {
        throw new Error(result.message || "Failed to save annotations");
      }

      showSnackbar({
        severity: result.status === "success" ? "success" : "warning",
        title:
          result.status === "success"
            ? t("annotator.annotation.snackbar.savedTitle", "Saved")
            : t("annotator.annotation.snackbar.partiallySavedTitle", "Partially Saved"),
        message: result.message || `Saved ${result.summary?.successful || 0} recording(s)`,
        duration: 3000,
      });

      if (Array.isArray(result.data)) result.data.forEach(applyAnnotationToState);

      window.scrollTo({ top: 0, behavior: "smooth" });

      if (result.summary?.successful > 0) {
        handleNextTab();
      }
    } catch (error) {
      console.error("Failed to save annotations in bulk:", error);
      showSnackbar({
        severity: "error",
        title: t("annotator.annotation.snackbar.saveFailedTitle", "Save Failed"),
        message:
          error.message ||
          t(
            "annotator.annotation.snackbar.saveBulkFailedMessage",
            "Failed to save annotations. Please try again."
          ),
      });
    } finally {
      setBulkSaving(false);
    }
  };

  // Copy All button function
  const handleCopyAllInCurrentTab = () => {
    if (isExamSubmitted) {
      showSnackbar({
        severity: "error",
        title: t("annotator.annotation.snackbar.examSubmittedTitle", "Exam Submitted"),
        message: t(
          "annotator.annotation.snackbar.cannotCopySubmittedMessage",
          "Cannot copy - exam is already submitted"
        ),
      });
      return;
    }

    if (!activeRecordings.length) {
      showSnackbar({
        severity: "info",
        title: t("annotator.annotation.snackbar.noRecordingsTitle", "No Recordings"),
        message: t(
          "annotator.annotation.snackbar.noRecordingsToCopyMessage",
          "No recordings to copy in this tab"
        ),
      });
      return;
    }

    const editableRecordings = activeRecordings.filter((rec) => canAnnotate(rec));

    if (editableRecordings.length === 0) {
      showSnackbar({
        severity: "warning",
        title: t("annotator.annotation.snackbar.noPermissionTitle", "No Permission"),
        message: t(
          "annotator.annotation.snackbar.noPermissionMessage",
          "You don't have permission to edit any recordings in this tab"
        ),
      });
      return;
    }

    setGroupedRecordings((prev) => {
      const newGrouped = { ...prev };
      const updatedRecordings = newGrouped[activeType].map((rec) => {
        if (!canAnnotate(rec)) {
          return rec;
        }

        const isPassage = rec?.is_passage === 1 || rec?.is_passage === "1";
        const canonicalLines = splitTextIntoLines(rec.sample_text, isPassage);

        const newTranscriptionLines = canonicalLines.map((line) => line);

        return {
          ...rec,
          annotation: {
            ...rec.annotation,
            transcription_lines: newTranscriptionLines,
            transcription_text: joinLinesIntoText(newTranscriptionLines),
          },
        };
      });

      newGrouped[activeType] = updatedRecordings;
      return newGrouped;
    });

    showSnackbar({
      severity: "success",
      title: t("annotator.annotation.snackbar.copiedAllTitle", "Copied All"),
      message: `Copied canonical text for ${editableRecordings.length} recording(s)`,
      duration: 2000,
    });

    setIsClearMode(true);
  };

  // Clear All button function: empties transcription for all editable recordings in the current tab
  const handleClearAllInCurrentTab = () => {
    if (isExamSubmitted) {
      showSnackbar({
        severity: "error",
        title: t("annotator.annotation.snackbar.examSubmittedTitle", "Exam Submitted"),
        message: t(
          "annotator.annotation.snackbar.cannotClearSubmittedMessage",
          "Cannot clear - exam is already submitted"
        ),
      });
      return;
    }

    if (!activeRecordings.length) {
      showSnackbar({
        severity: "info",
        title: t("annotator.annotation.snackbar.noRecordingsTitle", "No Recordings"),
        message: t(
          "annotator.annotation.snackbar.noRecordingsToClearMessage",
          "No recordings to clear in this tab"
        ),
      });
      return;
    }

    const editableRecordings = activeRecordings.filter((rec) => canAnnotate(rec));

    if (editableRecordings.length === 0) {
      showSnackbar({
        severity: "warning",
        title: t("annotator.annotation.snackbar.noPermissionTitle", "No Permission"),
        message: t(
          "annotator.annotation.snackbar.noPermissionMessage",
          "You don't have permission to edit any recordings in this tab"
        ),
      });
      return;
    }

    setGroupedRecordings((prev) => {
      const newGrouped = { ...prev };
      const updatedRecordings = newGrouped[activeType].map((rec) => {
        if (!canAnnotate(rec)) {
          return rec;
        }

        const isPassage = rec?.is_passage === 1 || rec?.is_passage === "1";
        const canonicalLines = splitTextIntoLines(rec.sample_text, isPassage);

        const newTranscriptionLines = canonicalLines.map(() => "");

        return {
          ...rec,
          annotation: {
            ...rec.annotation,
            transcription_lines: newTranscriptionLines,
            transcription_text: "",
          },
        };
      });

      newGrouped[activeType] = updatedRecordings;
      return newGrouped;
    });

    showSnackbar({
      severity: "success",
      title: t("annotator.annotation.snackbar.clearedAllTitle", "Cleared All"),
      message: `Cleared transcription for ${editableRecordings.length} recording(s)`,
      duration: 2000,
    });

    setIsClearMode(false);
  };

  const handleOpenCompleteConfirm = () => setConfirmCompleteOpen(true);
  const handleCloseCompleteConfirm = () => {
    if (!markingComplete) setConfirmCompleteOpen(false);
  };

  const handleConfirmComplete = async () => {
    if (!annotatorId || !examId) {
      showSnackbar({
        severity: "error",
        title: t("annotator.annotation.snackbar.missingDataTitle", "Missing data"),
        message: t(
          "annotator.annotation.snackbar.missingDataMessage",
          "Missing annotator or exam id."
        ),
      });
      return;
    }

    if (isExamSubmitted) {
      showSnackbar({
        severity: "error",
        title: t("annotator.annotation.snackbar.alreadySubmittedTitle", "Already Submitted"),
        message: t(
          "annotator.annotation.snackbar.alreadySubmittedMessage",
          "Exam is already submitted"
        ),
      });
      return;
    }

    try {
      setMarkingComplete(true);
      const resp = await markExamComplete({ annotator_id: annotatorId, exam_id: examId });
      if (resp?.status === "success") {
        showSnackbar({
          severity: "success",
          title: t("annotator.annotation.snackbar.completedTitle", "Completed"),
          message: t("annotator.annotation.snackbar.completedMessage", "Exam marked complete."),
        });

        // Always redirect to exams page after marking complete
        const targetTab = 2;
        navigate("/annotator/exams", {
          state: {
            preservedTab: targetTab,
            showSuccessMessage: true,
            completedExamId: examId,
            timestamp: Date.now(),
          },
        });
      } else {
        throw new Error(
          resp?.message ||
            t("annotator.annotation.errors.failedToMarkComplete", "Failed to mark complete")
        );
      }
    } catch (e) {
      console.error(e);
      showSnackbar({
        severity: "error",
        title: t("annotator.annotation.snackbar.failedTitle", "Failed"),
        message:
          e.message ||
          t("annotator.annotation.errors.failedToMarkComplete", "Failed to mark complete"),
      });
    } finally {
      setMarkingComplete(false);
      setConfirmCompleteOpen(false);
    }
  };

  const allSubmitted = useMemo(() => {
    if (loading || !Object.keys(groupedRecordings).length) return false;

    for (const sampleType in groupedRecordings) {
      const recordings = groupedRecordings[sampleType];
      const assignedRecordings = recordings.filter((rec) => {
        const isA1 = String(rec.annotator1_id ?? "") === String(annotatorId);
        const isA2 = String(rec.annotator2_id ?? "") === String(annotatorId);
        return isA1 || isA2;
      });

      if (assignedRecordings.length > 0) {
        const allInTypeSubmitted = assignedRecordings.every((rec) => {
          const isA1 = String(rec.annotator1_id ?? "") === String(annotatorId);
          const myStatus = isA1 ? rec.annotation1_status : rec.annotation2_status;
          return (myStatus || "").toLowerCase() === "submitted";
        });
        if (!allInTypeSubmitted) return false;
      }
    }

    return true;
  }, [groupedRecordings, loading, annotatorId]);

  // Exam can't be marked complete while any recording still has an unresolved admin remark
  const hasUnresolvedRemarks = useMemo(
    () =>
      Object.values(groupedRecordings).some((recordings) =>
        (recordings || []).some((rec) => String(rec.annotation?.remarks || "").trim() !== "")
      ),
    [groupedRecordings]
  );

  const completeDisabled = !allSubmitted || markingComplete || hasUnresolvedRemarks;

  const currentIndex = sampleTypes.indexOf(activeType);
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < sampleTypes.length - 1;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box p={3} bgcolor="#fafafa" borderRadius={2} border="1px solid #ddd">
              {/* UPDATED: Sample Details with Navigation */}
              <Card sx={{ p: 2, mb: 3, position: "relative" }}>
                <Grid container spacing={2} alignItems="center">
                  {/* Navigation Buttons on Left */}
                  <Grid item xs={1}>
                    <Box display="flex" gap={1} flexDirection="column">
                      <Tooltip
                        title={t(
                          "annotator.annotation.tooltips.previousExam",
                          "Previous Exam (Ctrl + ←)"
                        )}
                      >
                        <span>
                          <IconButton
                            onClick={handlePreviousExam}
                            disabled={!adjacentExams.prev_exam?.exam_id}
                            size="medium"
                            sx={{
                              border: "1px solid #ddd",
                              borderRadius: "8px",
                              bgcolor: adjacentExams.prev_exam?.exam_id ? "white" : "#f5f5f5",
                              "&:hover": {
                                bgcolor: adjacentExams.prev_exam?.exam_id ? "#f0f0f0" : "#f5f5f5",
                              },
                              width: "100%",
                              height: 50,
                            }}
                          >
                            <ArrowBackIcon fontSize="medium" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Grid>

                  {/* Exam Info */}
                  <Grid item xs={10}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Box mt={1}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2">
                              {t("annotator.annotation.labels.sessionId", "Sample ID")}:{" "}
                              {examId || "N/A"}
                            </Typography>
                            <Tooltip
                              title={t(
                                "annotator.annotation.tooltips.copySessionId",
                                "Copy Sample ID"
                              )}
                            >
                              <IconButton
                                size="small"
                                onClick={() => {
                                  navigator.clipboard.writeText(examId);
                                  showSnackbar({
                                    severity: "success",
                                    title: t("annotator.annotation.snackbar.copiedTitle", "Copied"),
                                    message: t(
                                      "annotator.annotation.snackbar.sessionIdCopiedMessage",
                                      "Sample ID copied to clipboard"
                                    ),
                                    duration: 2000,
                                  });
                                }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Right Navigation */}
                  <Grid item xs={1}>
                    <Box display="flex" gap={1} flexDirection="column">
                      <Tooltip
                        title={t("annotator.annotation.tooltips.nextExam", "Next Exam (Ctrl + →)")}
                      >
                        <span>
                          <IconButton
                            onClick={handleNextExam}
                            disabled={!adjacentExams.next_exam?.exam_id}
                            size="medium"
                            sx={{
                              border: "1px solid #ddd",
                              borderRadius: "8px",
                              bgcolor: adjacentExams.next_exam?.exam_id ? "white" : "#f5f5f5",
                              "&:hover": {
                                bgcolor: adjacentExams.next_exam?.exam_id ? "#f0f0f0" : "#f5f5f5",
                              },
                              width: "100%",
                              height: 50,
                            }}
                          >
                            <ArrowForwardIcon fontSize="medium" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Grid>
                </Grid>
              </Card>

              <>
                {/* Custom-styled tabs as buttons */}
                <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
                  <Typography variant="h6" mb={1} fontWeight="bold" color="#333">
                    {t("annotator.annotation.labels.verifySamples", "Verify Samples")} :
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1} mb={3}>
                    {sampleTypes.map((sampleType, i) => {
                      const isActive = sampleType === activeType;
                      const { status: myStatus, saved, total } = getMySectionProgress(sampleType);
                      const barColor =
                        myStatus === "completed"
                          ? "#2e7d32"
                          : myStatus === "inProgress"
                          ? "#ed6c02"
                          : "#9e9e9e";
                      return (
                        <Button
                          key={i}
                          onClick={() => setActiveType(sampleType)}
                          variant="outlined"
                          sx={{
                            borderRadius: 1,
                            boxShadow: isActive ? "0 4px 0 0 blue" : `0 4px 0 0 ${barColor}`,
                            border: "1px solid #ccc",
                            minWidth: 110,
                            color: "#000",
                            textTransform: "none",
                            fontSize: "15px",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            px: 1.5,
                          }}
                        >
                          {myStatus === "completed" ? (
                            <CheckCircleIcon fontSize="small" sx={{ color: "#2e7d32" }} />
                          ) : myStatus === "inProgress" ? (
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: "#ed6c02",
                                flexShrink: 0,
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                border: "1.5px solid #9e9e9e",
                                flexShrink: 0,
                              }}
                            />
                          )}
                          <span>{getSampleTypeLabel(sampleType)}</span>
                          {total > 0 && (
                            <Box
                              component="span"
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 700,
                                px: 0.75,
                                py: 0.1,
                                borderRadius: 1,
                                bgcolor:
                                  myStatus === "completed"
                                    ? "#e8f5e9"
                                    : myStatus === "inProgress"
                                    ? "#fff3e0"
                                    : "#f5f5f5",
                                color:
                                  myStatus === "completed"
                                    ? "#1b5e20"
                                    : myStatus === "inProgress"
                                    ? "#b25600"
                                    : "#616161",
                                ml: 0.25,
                              }}
                            >
                              {saved}/{total}
                            </Box>
                          )}
                        </Button>
                      );
                    })}
                  </Box>
                </Box>

                {/* Annotation Grid */}
                <Box
                  bgcolor="#fff"
                  px={2}
                  py={0}
                  sx={{
                    boxShadow: 4,
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid #e0e0e0",
                    mt: 2,
                  }}
                >
                  {!hasAnyPassage && (
                    <HeaderRow showDisfluency={hasDisfluency} showMistakes={hasNoMistakes} />
                  )}

                  {activeRecordings.map((row, i) => (
                    <AnnotationRow
                      key={row.recording_id}
                      row={row}
                      index={i}
                      annotatorId={annotatorId}
                      onAnnotationChange={handleAnnotationChange}
                      canAnnotate={canAnnotate}
                      savingRecord={savingRecord}
                      onSaveRecord={handleSaveRecord}
                      isExamSubmitted={isExamSubmitted}
                      onViewRemark={handleViewRemark}
                    />
                  ))}
                  {/* Footer Actions */}
                  <Box py={3} display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" gap={2}>
                      <Button
                        variant="contained"
                        startIcon={<NavigateBeforeIcon />}
                        onClick={handlePreviousTab}
                        disabled={!hasPrevious}
                        sx={{
                          fontWeight: "700",
                          padding: "10px 26px",
                          textTransform: "none",
                          fontSize: "0.95rem",
                          borderRadius: "10px",
                          background: (theme) =>
                            hasPrevious
                              ? "linear-gradient(180deg,#20c997 0%,#17a589 100%)"
                              : "#f0f0f0",
                          color: hasPrevious ? "#fff" : "#9aa0a6",
                          boxShadow: (theme) =>
                            hasPrevious ? "0 6px 14px rgba(32,201,151,0.18)" : "none",
                          transition: "all 180ms ease",
                          "&:hover": {
                            background: hasPrevious
                              ? "linear-gradient(180deg,#17a589 0%,#139077 100%)"
                              : "#f0f0f0",
                            boxShadow: hasPrevious ? "0 8px 20px rgba(23,160,137,0.18)" : "none",
                          },
                          "&:active": { transform: hasPrevious ? "translateY(1px)" : "none" },
                          "&.Mui-disabled": { opacity: 0.6, pointerEvents: "none" },
                        }}
                      >
                        {t("annotator.annotation.buttons.previous", "Previous")}
                      </Button>

                      {/* Copy All / Clear All toggle button */}
                      <Button
                        variant="outlined"
                        startIcon={isClearMode ? <ClearAllIcon /> : <ContentCopyIcon />}
                        onClick={
                          isClearMode ? handleClearAllInCurrentTab : handleCopyAllInCurrentTab
                        }
                        disabled={
                          isExamSubmitted || !activeRecordings.some((rec) => canAnnotate(rec))
                        }
                        sx={{
                          fontWeight: "700",
                          padding: "10px 26px",
                          textTransform: "none",
                          fontSize: "0.95rem",
                          borderRadius: "10px",
                          borderColor: isClearMode ? "#dd1d1d" : "#1d57dd",
                          color: isExamSubmitted ? "#9aa0a6" : isClearMode ? "#dd1d1d" : "#1d57dd",
                          "&:hover": {
                            borderColor: isClearMode ? "#b81616" : "#1648b8",
                            backgroundColor: isClearMode
                              ? "rgba(221, 29, 29, 0.04)"
                              : "rgba(29, 87, 221, 0.04)",
                          },
                          "&.Mui-disabled": {
                            opacity: 0.6,
                            pointerEvents: "none",
                          },
                        }}
                      >
                        {isClearMode
                          ? t("annotator.annotation.buttons.clearAll", "Clear All")
                          : t("annotator.annotation.buttons.copyAll", "Copy All")}
                      </Button>
                    </Box>

                    <Box display="flex" gap={2}>
                      {!isMyExamCompleted && (
                        <Tooltip
                          title={
                            hasUnresolvedRemarks
                              ? t(
                                  "annotator.annotation.tooltips.resolveRemarksFirst",
                                  "Resolve all admin remarks before marking the exam complete"
                                )
                              : ""
                          }
                          placement="top"
                        >
                          <span>
                            <Button
                              variant="contained"
                              onClick={handleOpenCompleteConfirm}
                              disabled={completeDisabled}
                              sx={{
                                fontWeight: "700",
                                padding: "10px 26px",
                                textTransform: "none",
                                fontSize: "0.95rem",
                                borderRadius: "10px",
                                background: (theme) =>
                                  completeDisabled
                                    ? "#f0f0f0"
                                    : "linear-gradient(180deg,#6f42c1 0%,#5a31a8 100%)",
                                color: (theme) => (completeDisabled ? "#9aa0a6" : "#fff"),
                                boxShadow: (theme) =>
                                  completeDisabled ? "none" : "0 6px 18px rgba(111,66,193,0.18)",
                                transition: "all 180ms ease",
                                "&:hover": {
                                  background: completeDisabled
                                    ? "#f0f0f0"
                                    : "linear-gradient(180deg,#5a31a8 0%,#4a248f 100%)",
                                },
                                "&.Mui-disabled": {
                                  opacity: 0.6,
                                  pointerEvents: "none",
                                },
                              }}
                            >
                              {t(
                                "annotator.annotation.buttons.markExamComplete",
                                "Mark Exam Complete"
                              )}
                            </Button>
                          </span>
                        </Tooltip>
                      )}

                      {/* Conditional button: Save & Next for non-last tabs, Save for last tab */}
                      {hasNext ? (
                        <Button
                          variant="contained"
                          endIcon={
                            bulkSaving ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : isExamSubmitted ? null : (
                              <NavigateNextIcon />
                            )
                          }
                          onClick={handleSaveAndNext}
                          disabled={bulkSaving || isExamSubmitted}
                          sx={{
                            fontWeight: "700",
                            padding: "10px 26px",
                            textTransform: "none",
                            fontSize: "0.95rem",
                            borderRadius: "10px",
                            background: (theme) =>
                              bulkSaving || isExamSubmitted
                                ? "#f0f0f0"
                                : "linear-gradient(180deg,#1d57dd 0%,#1648b8 100%)",
                            color: (theme) => (bulkSaving || isExamSubmitted ? "#9aa0a6" : "#fff"),
                            boxShadow: (theme) =>
                              bulkSaving || isExamSubmitted
                                ? "none"
                                : "0 6px 18px rgba(29,87,221,0.18)",
                            transition: "all 180ms ease",
                            "&:hover": {
                              background:
                                bulkSaving || isExamSubmitted
                                  ? "#f0f0f0"
                                  : "linear-gradient(180deg,#1648b8 0%,#123a94 100%)",
                            },
                            "&:active": {
                              transform:
                                !bulkSaving && !isExamSubmitted ? "translateY(1px)" : "none",
                            },
                            "&.Mui-disabled": { opacity: 0.6, pointerEvents: "none" },
                          }}
                        >
                          {bulkSaving
                            ? t("annotator.annotation.buttons.saving", "Saving...")
                            : isExamSubmitted
                            ? t("annotator.annotation.buttons.submitted", "Submitted")
                            : t("annotator.annotation.buttons.saveAndNext", "Save & Next")}
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          startIcon={
                            bulkSaving ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : isExamSubmitted ? null : (
                              <SaveIcon />
                            )
                          }
                          onClick={handleSaveCurrentTab}
                          disabled={bulkSaving || isExamSubmitted}
                          sx={{
                            fontWeight: "700",
                            padding: "10px 26px",
                            textTransform: "none",
                            fontSize: "0.95rem",
                            borderRadius: "10px",
                            background: (theme) =>
                              bulkSaving || isExamSubmitted
                                ? "#f0f0f0"
                                : "linear-gradient(180deg,#20c997 0%,#17a589 100%)",
                            color: (theme) => (bulkSaving || isExamSubmitted ? "#9aa0a6" : "#fff"),
                            boxShadow: (theme) =>
                              bulkSaving || isExamSubmitted
                                ? "none"
                                : "0 6px 18px rgba(32,201,151,0.18)",
                            transition: "all 180ms ease",
                            "&:hover": {
                              background:
                                bulkSaving || isExamSubmitted
                                  ? "#f0f0f0"
                                  : "linear-gradient(180deg,#17a589 0%,#139077 100%)",
                            },
                            "&:active": {
                              transform:
                                !bulkSaving && !isExamSubmitted ? "translateY(1px)" : "none",
                            },
                            "&.Mui-disabled": { opacity: 0.6, pointerEvents: "none" },
                          }}
                        >
                          {bulkSaving
                            ? t("annotator.annotation.buttons.saving", "Saving...")
                            : isExamSubmitted
                            ? t("annotator.annotation.buttons.submitted", "Submitted")
                            : t("annotator.annotation.buttons.save", "Save")}
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Box>
              </>
            </Box>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Confirmation Dialog for Mark Exam Complete */}
      <Dialog open={confirmCompleteOpen} onClose={handleCloseCompleteConfirm}>
        <DialogTitle>
          {t("annotator.annotation.dialogs.confirmCompletion.title", "Confirm Exam Completion")}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t(
              "annotator.annotation.dialogs.confirmCompletion.message",
              "Are you sure you want to mark this exam as complete? This action cannot be undone."
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCompleteConfirm} disabled={markingComplete}>
            {t("annotator.annotation.buttons.cancel", "Cancel")}
          </Button>
          <Button
            onClick={handleConfirmComplete}
            color="error"
            variant="contained"
            disabled={markingComplete}
          >
            {markingComplete ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t("annotator.annotation.buttons.confirmComplete", "Confirm Complete")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Remark Dialog */}
      <Dialog
        open={remarkDialogOpen}
        onClose={() => setRemarkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <CommentIcon color="info" />
              <Typography variant="h6">
                {t("annotator.annotation.dialogs.remark.title", "Remark from Admin")}
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setRemarkDialogOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography
            variant="body2"
            sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6, fontSize: "0.85rem" }}
          >
            {remarkDialogContent}
          </Typography>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={handleResolveRemark}
            variant="gradient"
            color="success"
            disabled={resolvingRemark || !remarkDialogRecordingId}
          >
            {resolvingRemark ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t("annotator.annotation.buttons.resolveRemark", "Resolve Remark")
            )}
          </MDButton>
          <Button onClick={() => setRemarkDialogOpen(false)} variant="contained" color="info">
            {t("annotator.annotation.buttons.close", "Close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        sx={{
          top: { xs: 8, sm: 16, md: 24 },
          right: { xs: 8, sm: 16, md: 24 },
          left: { xs: 8, sm: "auto" },
          maxWidth: { xs: "calc(100vw - 16px)", sm: 480 },
        }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          elevation={6}
          variant="filled"
          action={
            <IconButton size="small" aria-label="close" color="inherit" onClick={closeSnackbar}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }
          sx={{
            width: "100%",
            minWidth: { xs: 0, sm: 320 },
            borderRadius: 2,
            boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            alignItems: "flex-start",
            p: 0,
            overflow: "hidden",
            "& .MuiAlert-icon": {
              pt: 1.5,
              pl: 1.5,
              alignSelf: "flex-start",
            },
            "& .MuiAlert-message": {
              flex: 1,
              minWidth: 0,
              py: 1,
              px: 0,
            },
            "& .MuiAlert-action": {
              pt: 1,
              pr: 1,
              alignSelf: "flex-start",
            },
          }}
        >
          <Box sx={{ px: 1, py: 0.5 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: "#fff",
                fontWeight: 700,
                lineHeight: 1.3,
                wordBreak: "break-word",
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
              }}
            >
              {snackbar.title ||
                (snackbar.severity === "error"
                  ? t("annotator.annotation.snackbar.errorFallback", "Error")
                  : t("annotator.annotation.snackbar.successFallback", "Success"))}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "rgba(255,255,255,0.95)",
                mt: 0.5,
                lineHeight: 1.45,
                wordBreak: "break-word",
                whiteSpace: "pre-wrap",
                fontSize: { xs: "0.75rem", sm: "0.825rem" },
              }}
            >
              {snackbar.message}
            </Typography>
          </Box>
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
